import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUserCard } from '../hooks/useUserCard';
import Layout from '../components/Layout';
import VisitingCardDisplay from '../components/VisitingCardDisplay';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';
import ScanLimitDialog from '../components/ScanLimitDialog';
import DuplicateContactDialog from '../components/DuplicateContactDialog';
import { Search, UserPlus, Users, Trash2, StickyNote, Eye, Share2, Edit, Download, Upload, Scan, Crop, Mail, Phone, Globe, MapPin, Linkedin, Twitter, Github, Instagram, Youtube, Facebook, MessageCircle, Copy, FileText, Folder, FolderPlus, Settings, X, Check, Tag, Handshake, ChevronDown, Loader2, CheckCircle2, FileUp, FileDown, CreditCard, Smartphone } from 'lucide-react';
import { contactGroupsAPI } from '../services/contactGroupsAPI';
import { crmAPI, contactAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { mockContacts } from '../mockData';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { generateCanonicalUrl } from '../utils/seo';
import { getBreadcrumbSchema } from '../utils/structuredData';
import Cropper from 'react-easy-crop';

const Contacts = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { userCard, loading: cardLoading } = useUserCard();
  const navigate = useNavigate();
  const location = useLocation();
  const [contacts, setContacts] = useState([]);
  const [scannedContacts, setScannedContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [notesDialog, setNotesDialog] = useState({ open: false, contact: null, notes: '' });
  const [editScannedDialog, setEditScannedDialog] = useState({ open: false, contact: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, contactData: null, name: '' });
  const [loading, setLoading] = useState(true);
  const [showScanModal, setShowScanModal] = useState(false);
  const [scanLimitDialog, setScanLimitDialog] = useState({ open: false, scansUsed: 0, maxScans: 10, contactEmail: '' });
  const [canScan, setCanScan] = useState(true);
  const [scansRemaining, setScansRemaining] = useState(null);
  const [duplicateDialog, setDuplicateDialog] = useState({ 
    open: false, 
    existingContact: null, 
    newContact: null, 
    newContactData: null 
  });
  
  // CRM Deal Capture Dialog
  const [showCRMDialog, setShowCRMDialog] = useState(false);
  const [selectedContactForCRM, setSelectedContactForCRM] = useState(null);
  const [crmPipelines, setCrmPipelines] = useState([]);
  const [crmSelectedPipeline, setCrmSelectedPipeline] = useState('');
  const [crmStages, setCrmStages] = useState([]);
  const [crmDealName, setCrmDealName] = useState('');
  const [crmDealDescription, setCrmDealDescription] = useState('');
  const [crmDealAmount, setCrmDealAmount] = useState('');
  const [crmSelectedStage, setCrmSelectedStage] = useState('');
  
  // Manual Contact Creation Dialog
  const [showManualContactDialog, setShowManualContactDialog] = useState(false);
  const [manualContactData, setManualContactData] = useState({
    name: '',
    title: '',
    company: '',
    email: '',
    phone: '',
    websiteName: '',
    websiteTLD: '.com',
    location: '',
    locationAddress: '',
    bio: '',
    linkedin: '',
    twitter: '',
    github: '',
    instagram: '',
    youtube: '',
    facebook: '',
    whatsapp: '',
    notes: ''
  });
  const [savingManualContact, setSavingManualContact] = useState(false);
  
  // Duplicate management state
  const [showDuplicateManager, setShowDuplicateManager] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [selectedToKeep, setSelectedToKeep] = useState({});
  const [isProcessingDuplicates, setIsProcessingDuplicates] = useState(false);
  
  // Pagination state for scanned contacts
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalScannedContacts, setTotalScannedContacts] = useState(0);
  const itemsPerPage = 10;
  
  // Tab state - 'scanned' (card contacts) or 'digital'
  const [activeTab, setActiveTab] = useState('scanned');
  
  // Pagination loading state
  const [paginationLoading, setPaginationLoading] = useState(false);
  
  // Creator filter state
  const [creatorFilter, setCreatorFilter] = useState([]);  // Array for multi-select
  const [showCreatorFilterDropdown, setShowCreatorFilterDropdown] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [team, setTeam] = useState(null);
  
  // Ref to track previous scanned contacts for duplicate detection
  const previousScannedRef = useRef([]);
  
  // Group state
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupFormData, setGroupFormData] = useState({ name: '', color: '#3B82F6', icon: 'folder' });
  const [editingGroup, setEditingGroup] = useState(null);
  const [showGroupSelector, setShowGroupSelector] = useState(false);
  const [contactForGroupAssignment, setContactForGroupAssignment] = useState(null);
  
  // Scanner state
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  
  // Crop state
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImageType, setCropImageType] = useState(null); // 'front' or 'back'
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1.5); // Start at 1.5x zoom to fill more screen width
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedFrontImage, setCroppedFrontImage] = useState(null);
  const [croppedBackImage, setCroppedBackImage] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    company: '',
    email: '',
    phone: '',
    websiteName: '',
    websiteTLD: '',
    location: '',
    locationAddress: '',
    bio: '',
    linkedin: '',
    twitter: '',
    github: '',
    instagram: '',
    youtube: '',
    facebook: '',
    whatsapp: ''
  });
  
  // Import contacts state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importStatus, setImportStatus] = useState(null);
  const fileInputRef = useRef(null);

  // Load team data for creator filter
  const loadTeamData = useCallback(async () => {
    if (!user) return;
    
    try {
      const { teamAPI } = await import('../services/api');
      const teamData = await teamAPI.getMyTeam();
      setTeam(teamData);
      setTeamMembers(teamData.members || []);
      console.log('✅ Team data loaded:', teamData);
    } catch (error) {
      console.error('Error loading team data:', error);
      setTeam(null);
      setTeamMembers([]);
    }
  }, [user]);

  // Migrate existing contacts to add created_by field (one-time)
  const migrateCreatedByField = useCallback(async () => {
    if (!user) return;
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scanned-contacts/migrate-created-by`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log(`✅ Migrated ${data.contacts_updated} contacts with created_by field`);
      }
    } catch (error) {
      console.error('Error migrating contacts:', error);
    }
  }, [user]);

  // Define loadScannedContacts BEFORE useEffects that use it
  const loadScannedContacts = useCallback(async (forceRefresh = false, page = null, search = null, creators = null) => {
    if (!user) return;

    const targetPage = page !== null ? page : currentPage;
    const targetSearch = search !== null ? search : searchQuery;
    const targetCreators = creators !== null ? creators : creatorFilter;

    try {
      // Use cached API with pagination and search
      const { cachedScannedContactAPI } = await import('../services/cachedApi');
      const response = await cachedScannedContactAPI.getAll(
        user.uid, 
        forceRefresh, 
        targetPage, 
        itemsPerPage, 
        targetSearch,
        targetCreators.length > 0 ? targetCreators.join(',') : undefined
      );
      
      // Response structure: { data, total, page, limit, totalPages }
      setScannedContacts(response.data || []);
      setTotalScannedContacts(response.total || 0);
      setTotalPages(response.totalPages || 0);
      setCurrentPage(response.page || 1);
      setPaginationLoading(false);
      
      // Trigger update event for Dashboard and Home when contacts change
      if (forceRefresh) {
        localStorage.setItem('contact_updated', Date.now().toString());
        window.dispatchEvent(new Event('storage'));
        // Refresh groups to update contact counts
        loadGroups();
      }
    } catch (error) {
      console.error('Error loading scanned contacts:', error);
      setScannedContacts([]);
      setTotalScannedContacts(0);
      setTotalPages(0);
      setPaginationLoading(false);
    }
  }, [user, currentPage, searchQuery, itemsPerPage, creatorFilter]);

  useEffect(() => {
    if (cardLoading) {
      return; // Wait for auth to initialize
    }
    
    if (!user) {
      navigate('/');
      return;
    }
    
    // Load from cache immediately (no loading state needed)
    loadContacts(false); // false = use cache, instant load
    loadGroups(); // Load contact groups
    loadTeamData(); // Load team data for creator filter
    
    // Run migration once (it's idempotent - only updates contacts without created_by)
    migrateCreatedByField();
  }, [user, cardLoading, loadTeamData, migrateCreatedByField]);

  // Reload scanned contacts when page, search, or creator filter changes
  useEffect(() => {
    if (!user) return;
    
    const timer = setTimeout(() => {
      loadScannedContacts(true);
    }, searchQuery ? 500 : 0); // 500ms debounce for search, immediate for page/filter change
    
    return () => clearTimeout(timer);
  }, [currentPage, searchQuery, creatorFilter, user, loadScannedContacts]);

  // Close creator filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCreatorFilterDropdown && !event.target.closest('.creator-filter-dropdown')) {
        setShowCreatorFilterDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCreatorFilterDropdown]);

  // Smart polling: Only poll when there are processing contacts
  useEffect(() => {
    if (!user) return;
    
    // Check if there are any processing contacts
    const processingContacts = scannedContacts.filter(
      contact => contact.status === 'processing' || contact.isProcessing
    );
    
    if (processingContacts.length === 0) {
      // Update ref with current state when no processing
      previousScannedRef.current = scannedContacts;
      return;
    }
    
    console.log(`📊 Found ${processingContacts.length} contacts still processing, starting smart polling...`);
    console.log('Processing contacts:', processingContacts.map(c => ({
      id: c._id || c.id,
      name: c.name,
      status: c.status
    })));
    
    // Initialize ref if empty
    if (previousScannedRef.current.length === 0) {
      previousScannedRef.current = [...scannedContacts];
    }
    
    // Set up polling interval - check every 10 seconds (faster for testing)
    const pollInterval = setInterval(async () => {
      console.log('🔄 Checking processing contact status...');
      
      // Silent refresh from API (with current page and search)
      const { cachedScannedContactAPI } = await import('../services/cachedApi');
      const response = await cachedScannedContactAPI.getAll(user.uid, true, currentPage, itemsPerPage, searchQuery);
      const freshScanned = response.data || [];
      
      console.log(`📥 Fetched ${freshScanned.length} contacts from API`);
      
      // Detect newly completed contacts (status changed from 'processing' to 'completed')
      let duplicateFound = false;
      
      for (const freshContact of freshScanned) {
        const contactId = freshContact._id || freshContact.id;
        const previousContact = previousScannedRef.current.find(
          c => (c._id || c.id) === contactId
        );
        
        console.log(`Checking contact: ${freshContact.name || 'Unknown'}`);
        console.log(`  Previous status: ${previousContact?.status || 'N/A'}`);
        console.log(`  Current status: ${freshContact.status}`);
        console.log(`  Has data: name=${!!freshContact.name}, title=${!!freshContact.title}, company=${!!freshContact.company}`);
        
        // Check if this contact just finished processing
        if (previousContact && 
            (previousContact.status === 'processing' || previousContact.isProcessing) &&
            (freshContact.status === 'completed' || freshContact.status === 'success') && 
            !freshContact.isProcessing) {
          
          console.log('✅ Contact finished processing:', freshContact.name);
          console.log('📋 Contact data:', {
            name: freshContact.name,
            title: freshContact.title,
            company: freshContact.company,
            email: freshContact.email,
            phone: freshContact.phone
          });
          
          // Check for duplicates NOW that we have the extracted data
          console.log('🔍 Running duplicate check...');
          const duplicate = findDuplicate(freshContact);
          
          if (duplicate) {
            console.log('🚨 DUPLICATE DETECTED!');
            console.log('Existing contact:', duplicate);
            console.log('New contact:', freshContact);
            
            duplicateFound = true;
            
            // Show duplicate dialog
            setDuplicateDialog({
              open: true,
              existingContact: duplicate,
              newContact: freshContact,
              newContactData: { data: freshContact }
            });
            
            // Update ref but don't update UI yet, wait for user decision
            previousScannedRef.current = freshScanned;
            return;
          } else {
            console.log('✅ No duplicate found for:', freshContact.name);
          }
        }
      }
      
      // Update ref with current state
      previousScannedRef.current = freshScanned;
      
      // Update UI only if no duplicate was found (update full response)
      if (!duplicateFound) {
        setScannedContacts(freshScanned);
        setTotalScannedContacts(response.total || 0);
        setTotalPages(response.totalPages || 0);
      }
      
      // Check if any still processing
      const stillProcessing = freshScanned.some(
        c => c.status === 'processing' || c.isProcessing
      );
      
      if (!stillProcessing) {
        console.log('✅ All contacts processed! Stopping polling.');
      }
    }, 10000); // Poll every 10 seconds (faster for better UX)
    
    // Cleanup interval on unmount or when no more processing contacts
    return () => {
      console.log('🛑 Stopped polling for processing contacts');
      clearInterval(pollInterval);
    };
  }, [user, scannedContacts]);

  // Check scan limit (only once on mount, not on every scannedContacts change)
  useEffect(() => {
    const checkScanLimit = async () => {
      if (!user) return;
      
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/scan-limit`, {
          headers: {
            'Authorization': `Bearer ${await user.getIdToken()}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setCanScan(data.can_scan);
          setScansRemaining(data.scans_remaining);
          if (!data.can_scan) {
            setScanLimitDialog({
              open: false,
              scansUsed: data.scans_used,
              maxScans: data.max_scans,
              contactEmail: data.contact_email
            });
          }
        }
      } catch (error) {
        console.error('Error checking scan limit:', error);
      }
    };
    
    checkScanLimit();
  }, [user]); // Only run once when user changes, not on every scannedContacts.length change

  // Check for scan query parameter and open scan modal
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('scan') === 'true' && user) {
      if (canScan) {
        setShowScanModal(true);
      } else {
        setScanLimitDialog(prev => ({ ...prev, open: true }));
      }
      // Clean up URL
      navigate('/contacts', { replace: true });
    }
  }, [location, user, navigate, canScan]);


  // Load contact groups
  const loadGroups = async () => {
    try {
      const groupsData = await contactGroupsAPI.getAll();
      setGroups(groupsData);
    } catch (error) {
      console.error('Failed to load groups:', error);
    }
  };

  // Create or update group
  const saveGroup = async () => {
    try {
      if (editingGroup) {
        await contactGroupsAPI.update(editingGroup.id, groupFormData);
        toast({ title: 'Success', description: 'Group updated successfully' });
      } else {
        await contactGroupsAPI.create(groupFormData);
        toast({ title: 'Success', description: 'Group created successfully' });
      }
      loadGroups();
      setShowGroupModal(false);
      setGroupFormData({ name: '', color: '#3B82F6', icon: 'folder' });
      setEditingGroup(null);
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to save group',
        variant: 'destructive'
      });
    }
  };

  // Delete group
  const deleteGroup = async (groupId) => {
    if (!window.confirm('Are you sure? This will remove this group from all contacts.')) {
      return;
    }
    
    try {
      await contactGroupsAPI.delete(groupId);
      toast({ title: 'Success', description: 'Group deleted successfully' });
      loadGroups();
      if (selectedGroup === groupId) {
        setSelectedGroup('all');
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: 'Failed to delete group',
        variant: 'destructive'
      });
    }
  };

  // Assign contact to groups
  const assignContactToGroups = async (contactId, groupIds) => {
    try {
      console.log('🔍 Assigning contact to groups:', { contactId, groupIds });
      
      if (!contactId) {
        throw new Error('Contact ID is missing');
      }
      
      await contactGroupsAPI.assignContact(contactId, groupIds);
      toast({ title: 'Success', description: 'Contact groups updated' });
      
      // Reload groups to update counts
      await loadGroups();
      
      // Reload both digital and scanned contacts to reflect new group assignments
      loadContacts(true);
      loadScannedContacts(true);
      
      setShowGroupSelector(false);
      setContactForGroupAssignment(null);
    } catch (error) {
      console.error('❌ Group assignment error:', error);
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || error.message || 'Failed to update contact groups',
        variant: 'destructive'
      });
    }
  };

  const loadContacts = async (forceRefresh = false) => {
    if (!user) return;

    try {
      // Load both types of contacts:
      // 1. Relationship-based contacts (digital cards - live data, bidirectional)
      // 2. Old contacts (backward compatibility)
      // 3. Scanned physical cards (one-way)
      const { cachedContactAPI, cachedScannedContactAPI } = await import('../services/cachedApi');
      const { contactRelationshipAPI } = await import('../services/api');
      
      // Load all contact types in parallel
      const [relationshipContacts, oldContacts, scannedResponse] = await Promise.all([
        contactRelationshipAPI.getUserRelationships(user.uid).catch(() => []),
        cachedContactAPI.getUserContacts(user.uid, forceRefresh).catch(() => []),
        cachedScannedContactAPI.getAll(user.uid, forceRefresh, currentPage, itemsPerPage, searchQuery)
      ]);
      
      // Mark relationship contacts with a flag
      const markedRelationshipContacts = relationshipContacts.map(c => ({
        ...c,
        isRelationshipContact: true
      }));
      
      // Combine relationship contacts and old contacts
      // Filter out old contacts that are now in relationships (avoid duplicates)
      const relationshipUsernames = new Set(relationshipContacts.map(c => c.username));
      const filteredOldContacts = oldContacts.filter(c => !relationshipUsernames.has(c.username));
      
      // Mark old contacts with a flag
      const markedOldContacts = filteredOldContacts.map(c => ({
        ...c,
        isRelationshipContact: false
      }));
      
      // Merge: Relationship contacts first (they have live data), then old contacts
      const allDigitalContacts = [...markedRelationshipContacts, ...markedOldContacts];
      
      setContacts(allDigitalContacts);
      setScannedContacts(scannedResponse.data || []);
      setTotalScannedContacts(scannedResponse.total || 0);
      setTotalPages(scannedResponse.totalPages || 0);
      setLoading(false);
      
      // Background refresh only if there are processing contacts
      if (!forceRefresh) {
        const hasProcessing = (scannedResponse.data || []).some(
          c => c.status === 'processing' || c.isProcessing
        );
        
        if (hasProcessing) {
          // Silent background refresh to check processing status
          setTimeout(async () => {
            const freshScanned = await cachedScannedContactAPI.getAll(user.uid, true, currentPage, itemsPerPage, searchQuery);
            setScannedContacts(freshScanned.data || []);
            setTotalScannedContacts(freshScanned.total || 0);
            setTotalPages(freshScanned.totalPages || 0);
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
      toast({
        title: "Error loading contacts",
        description: error.response?.data?.detail || "Failed to load contacts. Please try again.",
        variant: "destructive"
      });
      setContacts([]);
      setScannedContacts([]);
      setTotalScannedContacts(0);
      setTotalPages(0);
      setLoading(false);
    }
  };

  const handleUpdateNotes = async (contactId, notes) => {
    if (!user) return;

    try {
      // Check contact type
      const isScannedContact = notesDialog.contact && (notesDialog.contact.frontImage || notesDialog.contact.frontImageUrl);
      const isRelationshipContact = notesDialog.contact && notesDialog.contact.isRelationshipContact;
      
      if (isScannedContact) {
        // Use scanned contact API
        const { scannedContactAPI } = await import('../services/api');
        await scannedContactAPI.update(contactId, { notes });
        await loadScannedContacts(true); // Force refresh
      } else if (isRelationshipContact) {
        // Use contact relationship API for new relationship contacts
        const { contactRelationshipAPI } = await import('../services/api');
        await contactRelationshipAPI.updateNotes(contactId, notes);
        await loadContacts(true); // Force refresh
      } else {
        // Use cached contact API for old contacts
        const { cachedContactAPI } = await import('../services/cachedApi');
        await cachedContactAPI.updateContactNotes(contactId, notes, user.uid);
        await loadContacts(true); // Force refresh
      }
      
      toast({
        title: "Notes Updated",
        description: "Contact notes have been saved."
      });
    } catch (error) {
      console.error('Error updating notes:', error);
      toast({
        title: "Error",
        description: "Failed to update notes. Please try again.",
        variant: "destructive"
      });
      throw error;
    }
  };

  const handleDelete = async (contactData) => {
    try {
      console.log('🗑️ Attempting to delete digital contact');
      console.log('  Contact data:', JSON.stringify(contactData, null, 2));
      console.log('👤 Current user ID:', user.uid);
      
      const { isRelationshipContact, username, dbId, relationship_id } = contactData;
      
      // Check if contact has any active deals in CRM
      const leads = await crmAPI.getLeads();
      console.log('📊 Total leads in CRM:', leads.length);
      
      // For digital contacts, check using relationship_id
      const contactId = isRelationshipContact ? relationship_id : dbId;
      console.log('🔍 Contact ID to check:', contactId);
      console.log('🔍 isRelationshipContact:', isRelationshipContact);
      
      if (contactId) {
        const contactDeals = leads.filter(lead => {
          const matches = lead.contactId === contactId;
          if (matches) {
            console.log('✅ Found matching deal:', lead.name, 'contactId:', lead.contactId);
          }
          return matches;
        });
        
        console.log('💼 Total deals for this contact:', contactDeals.length);
        
        if (contactDeals.length > 0) {
          console.log('🚫 Blocking deletion - contact has active deals');
          toast({
            title: "Cannot Delete Contact",
            description: `This contact has ${contactDeals.length} active deal(s) in CRM. Please complete or archive the deals before deleting the contact.`,
            variant: "destructive"
          });
          return;
        } else {
          console.log('✅ No deals found - allowing deletion');
        }
      } else {
        console.log('⚠️ No contactId found - skipping deal check');
      }
      
      if (isRelationshipContact) {
        // New system: Delete via contact-relationships endpoint using username
        if (!username) {
          throw new Error('Username is missing for relationship contact');
        }
        
        const deleteUrl = `${process.env.REACT_APP_BACKEND_URL}/api/contact-relationships/by-username/${username}`;
        console.log('📡 Delete URL (relationship):', deleteUrl);
        
        const deleteResponse = await fetch(deleteUrl, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${await user.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        });
        
        console.log('📡 Delete response status:', deleteResponse.status);
        
        if (!deleteResponse.ok) {
          const errorData = await deleteResponse.json().catch(() => ({}));
          console.error('❌ Delete failed:', errorData);
          throw new Error(errorData.detail || 'Failed to delete relationship');
        }
        
        const successData = await deleteResponse.json().catch(() => ({}));
        console.log('✅ Relationship deleted successfully:', successData);
      } else {
        // Old system: Delete via cached contacts API using MongoDB _id
        if (!dbId) {
          throw new Error('Database ID is missing for old contact');
        }
        
        console.log('📡 Deleting old contact via cachedContactAPI with dbId:', dbId);
        const { cachedContactAPI } = await import('../services/cachedApi');
        await cachedContactAPI.deleteContact(dbId, user.uid);
        console.log('✅ Old contact deleted successfully');
      }
      
      // Optimistically update UI - remove from contacts list immediately
      const contactIdToRemove = relationship_id || dbId;
      setContacts(prev => prev.filter(c => {
        const cId = c.relationship_id || c.id || c._id;
        return cId !== contactIdToRemove;
      }));
      
      // Also update groups to reflect the deletion
      setGroups(prevGroups => prevGroups.map(group => ({
        ...group,
        contactCount: Math.max(0, (group.contactCount || 0) - (group.contacts?.includes(contactIdToRemove) ? 1 : 0))
      })));
      
      // Force refresh contacts in background
      loadContacts(true);
      
      toast({
        title: "Contact Deleted",
        description: "Contact has been successfully removed."
      });
    } catch (error) {
      console.error('❌ Error deleting contact:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete contact. Please try again.",
        variant: "destructive"
      });
    }
  };

  const openDeleteDialog = (type, contactData, name) => {
    setDeleteDialog({ open: true, type, contactData, name });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, type: null, contactData: null, name: '' });
  };

  const confirmDelete = async () => {
    const { type, contactData } = deleteDialog;
    
    try {
      if (type === 'digital') {
        await handleDelete(contactData);
      } else if (type === 'scanned') {
        await deleteScannedContactConfirmed(contactData.id || contactData._id);
      }
      closeDeleteDialog();
    } catch (error) {
      // Error handling is done in the individual delete functions
      closeDeleteDialog();
    }
  };

  const deleteScannedContactConfirmed = async (contactId) => {
    try {
      // Check if contact has any active deals in CRM
      const leads = await crmAPI.getLeads();
      const contactDeals = leads.filter(lead => lead.contactId === contactId && lead.contactType === 'physical');
      
      if (contactDeals.length > 0) {
        toast({
          title: "Cannot Delete Contact",
          description: `This contact has ${contactDeals.length} active deal(s) in CRM. Please complete or archive the deals before deleting the contact.`,
          variant: "destructive"
        });
        return;
      }
      
      // Use cached API for deletion
      const { cachedScannedContactAPI } = await import('../services/cachedApi');
      await cachedScannedContactAPI.delete(contactId, user.uid);
      
      // Optimistically update UI - remove from scanned contacts immediately
      setScannedContacts(prev => prev.filter(c => {
        const cId = c.id || c._id;
        return cId !== contactId;
      }));
      
      // Also update groups to reflect the deletion
      setGroups(prevGroups => prevGroups.map(group => ({
        ...group,
        contactCount: Math.max(0, (group.contactCount || 0) - (group.contacts?.includes(contactId) ? 1 : 0))
      })));
      
      toast({
        title: "Contact Deleted",
        description: "Scanned contact has been removed."
      });
      
      // Force refresh in background
      loadScannedContacts(true);
    } catch (error) {
      console.error('Error deleting scanned contact:', error);
      toast({
        title: "Delete Failed",
        description: "Failed to delete contact. Please try again.",
        variant: "destructive"
      });
    }
  };

  const openNotesDialog = (contact) => {
    setNotesDialog({
      open: true,
      contact,
      notes: contact.notes || ''
    });
  };

  const closeNotesDialog = () => {
    setNotesDialog({ open: false, contact: null, notes: '' });
  };

  const saveNotes = async () => {
    if (!notesDialog.contact) return;

    try {
      // For relationship contacts, use relationship_id; for scanned contacts use _id; for old contacts use id
      const contactId = notesDialog.contact.relationship_id || notesDialog.contact._id || notesDialog.contact.id;
      await handleUpdateNotes(contactId, notesDialog.notes);
      closeNotesDialog();
    } catch (error) {
      // Error handling is done in handleUpdateNotes
    }
  };

  const openEditScannedDialog = (contact) => {
    setEditScannedDialog({
      open: true,
      contact: { ...contact }
    });
  };

  const closeEditScannedDialog = () => {
    setEditScannedDialog({ open: false, contact: null });
  };

  const saveScannedContact = async () => {
    if (!editScannedDialog.contact) return;

    try {
      const { scannedContactAPI } = await import('../services/api');
      
      const updateData = {
        name: editScannedDialog.contact.name,
        title: editScannedDialog.contact.title || '',
        company: editScannedDialog.contact.company || '',
        email: editScannedDialog.contact.email || '',
        phone: editScannedDialog.contact.phone || '',
        phone2: editScannedDialog.contact.phone2 || '',
        websiteName: editScannedDialog.contact.websiteName || '',
        websiteTLD: editScannedDialog.contact.websiteTLD || '',
        location: editScannedDialog.contact.location || '',
        locationAddress: editScannedDialog.contact.locationAddress || '',
        bio: editScannedDialog.contact.bio || '',
        linkedin: editScannedDialog.contact.linkedin || '',
        twitter: editScannedDialog.contact.twitter || '',
        github: editScannedDialog.contact.github || '',
        instagram: editScannedDialog.contact.instagram || '',
        youtube: editScannedDialog.contact.youtube || '',
        facebook: editScannedDialog.contact.facebook || '',
        whatsapp: editScannedDialog.contact.whatsapp || ''
      };

      await scannedContactAPI.update(editScannedDialog.contact._id, updateData);
      
      toast({
        title: "Contact Updated",
        description: "Scanned contact has been updated successfully.",
      });

      await loadScannedContacts();
      closeEditScannedDialog();
    } catch (error) {
      console.error('Error updating scanned contact:', error);
      toast({
        title: "Update Failed",
        description: "Failed to update scanned contact. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleAddToCRM = async (contact) => {
    try {
      // ✅ Navigate to CRM page with contact data
      navigate('/crm', {
        state: {
          addDealContact: {
            name: contact.name || 'Unknown',
            phone: contact.phone || null,
            email: contact.email || null,
            contactId: contact.contactType === 'digital' ? contact.relationship_id : (contact._id || contact.id),
            contactType: contact.contactType === 'digital' ? 'digital' : 'physical',
            title: contact.title || contact.designation || null,
            company: contact.company || null
          }
        }
      });
      
      console.log('🔄 Redirecting to CRM with contact:', {
        name: contact.name,
        contactType: contact.contactType
      });
    } catch (error) {
      console.error('Error navigating to CRM:', error);
      toast({
        title: 'Error',
        description: 'Failed to navigate to CRM',
        variant: 'destructive'
      });
    }
  };

  const handlePipelineChange = async (pipelineId) => {
    try {
      setCrmSelectedPipeline(pipelineId);
      
      // Load stages for selected pipeline
      const stages = await crmAPI.getStages();
      // ✅ Fix: Check both pipelineId and pipeline_id (backend uses camelCase)
      const pipelineStages = stages.filter(s => 
        s.pipelineId === pipelineId || s.pipeline_id === pipelineId
      );
      
      console.log('🔄 Pipeline changed:', {
        newPipelineId: pipelineId,
        totalStages: stages.length,
        filteredStages: pipelineStages.length,
        stageNames: pipelineStages.map(s => s.name)
      });
      
      // ✅ Don't show error toast - just update UI to show no stages available
      setCrmStages(pipelineStages);
      setCrmSelectedStage(pipelineStages.length > 0 ? pipelineStages[0].id : '');
    } catch (error) {
      console.error('Error loading pipeline stages:', error);
      toast({
        title: 'Error',
        description: 'Failed to load stages',
        variant: 'destructive'
      });
    }
  };

  const handleSaveToCRM = async () => {
    if (!selectedContactForCRM) return;

    try {
      // Determine contact type and ID based on the contact source
      const isDigital = selectedContactForCRM.contactType === 'digital';
      const contactId = isDigital 
        ? selectedContactForCRM.relationship_id  // For digital contacts, use relationship_id
        : selectedContactForCRM._id || selectedContactForCRM.id; // For scanned contacts
      
      // Create lead in CRM with deal details (keep only reference, don't copy all data)
      await crmAPI.createLead({
        name: selectedContactForCRM.name || 'Unknown',
        phone: selectedContactForCRM.phone || null,
        email: selectedContactForCRM.email || null,
        stageId: crmSelectedStage,
        contactId: contactId,
        contactType: isDigital ? 'digital' : 'physical',
        title: selectedContactForCRM.title || selectedContactForCRM.designation || null,
        company: selectedContactForCRM.company || null,
        isImportant: false,
        dealName: crmDealName || null,
        dealDescription: crmDealDescription || null,
        dealAmount: crmDealAmount ? parseFloat(crmDealAmount) : null
      });

      toast({
        title: 'Success!',
        description: `${selectedContactForCRM.name} added to CRM`,
      });

      setShowCRMDialog(false);
      
      // Redirect to CRM page
      navigate('/crm');
    } catch (error) {
      console.error('Error adding to CRM:', error);
      toast({
        title: 'Error',
        description: 'Failed to add contact to CRM',
        variant: 'destructive'
      });
    }
  };


  const handleShare = async (contact) => {
    const cardUrl = `${window.location.origin}/bio/${contact.username}`;
    const shareData = {
      title: `${contact.name}'s Digital Business Card`,
      text: `Check out ${contact.name}'s digital business card on Piper Card`,
      url: cardUrl
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        toast({
          title: "Shared Successfully!",
          description: "Contact card has been shared.",
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(cardUrl);
        toast({
          title: "Link Copied!",
          description: "Card link copied to clipboard. Share it anywhere!",
        });
      }
    } catch (error) {
      // User cancelled share or error occurred
      if (error.name !== 'AbortError') {
        console.error('Error sharing:', error);
        // Fallback: copy to clipboard
        try {
          await navigator.clipboard.writeText(cardUrl);
          toast({
            title: "Link Copied!",
            description: "Card link copied to clipboard. Share it anywhere!",
          });
        } catch (clipboardError) {
          toast({
            title: "Share Failed",
            description: "Unable to share or copy the link. Please try again.",
            variant: "destructive"
          });
        }
      }
    }
  };

  const handleCopyContactDetails = async (contact) => {
    try {
      // Build contact details text
      let details = [];
      
      if (contact.name) details.push(`Name: ${contact.name}`);
      if (contact.title) details.push(`Title: ${contact.title}`);
      if (contact.company) details.push(`Company: ${contact.company}`);
      if (contact.email) details.push(`Email: ${contact.email}`);
      if (contact.phone) details.push(`Mobile: ${contact.phone}`);
      if (contact.phone2) details.push(`Office: ${contact.phone2}`);
      
      // Build website URL
      if (contact.websiteName || contact.websiteTLD) {
        const website = `${contact.websiteName}${contact.websiteTLD || ''}`;
        details.push(`Website: ${website}`);
      }
      
      if (contact.location) details.push(`Location: ${contact.location}`);
      if (contact.locationAddress) details.push(`Address: ${contact.locationAddress}`);
      if (contact.bio) details.push(`Bio: ${contact.bio}`);
      
      // Add social links
      if (contact.linkedin) details.push(`LinkedIn: ${contact.linkedin}`);
      if (contact.twitter) details.push(`Twitter: ${contact.twitter}`);
      if (contact.github) details.push(`GitHub: ${contact.github}`);
      if (contact.instagram) details.push(`Instagram: ${contact.instagram}`);
      if (contact.facebook) details.push(`Facebook: ${contact.facebook}`);
      if (contact.youtube) details.push(`YouTube: ${contact.youtube}`);
      if (contact.whatsapp) details.push(`WhatsApp: ${contact.whatsapp}`);
      
      // Add notes if available
      if (contact.notes && contact.notes.trim()) {
        details.push(`\nNotes:\n${contact.notes}`);
      }
      
      const contactText = details.join('\n');
      
      await navigator.clipboard.writeText(contactText);
      
      toast({
        title: "Contact Details Copied!",
        description: "All contact information has been copied to clipboard.",
      });
    } catch (error) {
      console.error('Error copying contact details:', error);
      toast({
        title: "Copy Failed",
        description: "Unable to copy contact details. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Crop callback
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Create cropped image
  const createCroppedImage = async (imageSrc, pixelCrop) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.95);
    });
  };

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.src = url;
    });

  const handleCropSave = async () => {
    try {
      const croppedBlob = await createCroppedImage(cropImageSrc, croppedAreaPixels);
      const croppedFile = new File([croppedBlob], `cropped-${cropImageType}.jpg`, { type: 'image/jpeg' });
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(croppedBlob);
      
      if (cropImageType === 'front') {
        setFrontImage(croppedFile);
        setFrontPreview(previewUrl);
      } else {
        setBackImage(croppedFile);
        setBackPreview(previewUrl);
      }
      
      // Close crop modal
      setShowCropModal(false);
      setCropImageSrc(null);
      setCropImageType(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      
      toast({
        title: "Image Cropped!",
        description: "Focus area selected successfully."
      });
    } catch (error) {
      console.error('Error cropping image:', error);
      toast({
        title: "Crop Failed",
        description: "Failed to crop image. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Duplicate detection function
  const findDuplicate = (newContact) => {
    console.log('🔍 findDuplicate called with:', newContact);
    
    if (!newContact) {
      console.log('  ❌ No contact provided');
      return null;
    }
    
    const newName = (newContact.name || '').toLowerCase().trim();
    const newTitle = (newContact.title || '').toLowerCase().trim();
    const newCompany = (newContact.company || '').toLowerCase().trim();
    
    console.log('  New contact normalized:', { newName, newTitle, newCompany });
    
    if (!newName || !newTitle || !newCompany) {
      console.log('  ❌ Missing required fields (name, title, or company)');
      return null;
    }
    
    // Search in both regular contacts and scanned contacts
    const allContacts = [...contacts, ...scannedContacts];
    console.log(`  Searching through ${allContacts.length} existing contacts`);
    
    for (const contact of allContacts) {
      // Skip comparing with itself
      if ((contact._id || contact.id) === (newContact._id || newContact.id)) {
        console.log(`  ⏭️  Skipping self: ${contact.name}`);
        continue;
      }
      
      const existingName = (contact.name || '').toLowerCase().trim();
      const existingTitle = (contact.title || '').toLowerCase().trim();
      const existingCompany = (contact.company || '').toLowerCase().trim();
      
      // Check if all three fields match
      const nameMatch = newName && existingName && newName === existingName;
      const titleMatch = newTitle && existingTitle && newTitle === existingTitle;
      const companyMatch = newCompany && existingCompany && newCompany === existingCompany;
      
      if (nameMatch && titleMatch && companyMatch) {
        console.log('  ✅ MATCH FOUND!');
        console.log('    Existing:', { existingName, existingTitle, existingCompany });
        console.log('    New:', { newName, newTitle, newCompany });
        return contact;
      }
    }
    
    console.log('  ❌ No duplicate found');
    return null;
  };

  // Duplicate dialog handlers
  const handleReplaceDuplicate = async () => {
    try {
      const { existingContact, newContactData } = duplicateDialog;
      
      // Delete existing contact
      if (existingContact.frontImage || existingContact.frontImageUrl) {
        // It's a scanned contact
        const { cachedScannedContactAPI } = await import('../services/cachedApi');
        await cachedScannedContactAPI.delete(existingContact._id || existingContact.id, user.uid);
      } else {
        // It's a regular contact
        const { cachedContactAPI } = await import('../services/cachedApi');
        await cachedContactAPI.deleteContact(existingContact._id || existingContact.id, user.uid);
      }
      
      // Close dialogs
      setDuplicateDialog({ open: false, existingContact: null, newContact: null, newContactData: null });
      setShowScanModal(false);
      resetScanState();
      
      // Reload contacts
      await loadContacts(true);
      
      toast({
        title: "Contact Replaced",
        description: "The existing contact has been replaced with the new one.",
      });
    } catch (error) {
      console.error('Error replacing contact:', error);
      toast({
        title: "Error",
        description: "Failed to replace contact. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleAddDuplicateAsNew = async () => {
    // Just close the dialog and keep the new contact
    setDuplicateDialog({ open: false, existingContact: null, newContact: null, newContactData: null });
    setShowScanModal(false);
    resetScanState();
    
    // Reload to show both contacts
    await loadContacts(true);
    
    toast({
      title: "Contact Added",
      description: "The new contact has been added alongside the existing one.",
    });
  };

  const handleIgnoreDuplicate = async () => {
    try {
      const { newContact } = duplicateDialog;
      
      // Delete the newly scanned contact
      const { cachedScannedContactAPI } = await import('../services/cachedApi');
      await cachedScannedContactAPI.delete(newContact._id || newContact.id, user.uid);
      
      // Close dialogs
      setDuplicateDialog({ open: false, existingContact: null, newContact: null, newContactData: null });
      setShowScanModal(false);
      resetScanState();
      
      // Reload contacts
      await loadContacts(true);
      
      toast({
        title: "Card Ignored",
        description: "The scanned card has been discarded.",
      });
    } catch (error) {
      console.error('Error ignoring duplicate:', error);
      toast({
        title: "Error",
        description: "Failed to ignore card. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Bulk duplicate management functions
  const findAllDuplicates = async () => {
    try {
      setIsProcessingDuplicates(true);
      
      // Load ALL scanned contacts (no pagination)
      const { cachedScannedContactAPI } = await import('../services/cachedApi');
      const allContactsResponse = await cachedScannedContactAPI.getAll(user.uid, true, 1, 10000); // Get all
      const allContacts = allContactsResponse.data || [];
      
      // Group contacts by similarity
      const groups = [];
      const processed = new Set();
      
      for (let i = 0; i < allContacts.length; i++) {
        const contact1 = allContacts[i];
        const id1 = contact1._id;
        
        if (processed.has(id1)) continue;
        
        const group = [contact1];
        processed.add(id1);
        
        // Find similar contacts
        for (let j = i + 1; j < allContacts.length; j++) {
          const contact2 = allContacts[j];
          const id2 = contact2._id;
          
          if (processed.has(id2)) continue;
          
          if (areDuplicates(contact1, contact2)) {
            group.push(contact2);
            processed.add(id2);
          }
        }
        
        // Only add groups with 2+ contacts
        if (group.length > 1) {
          groups.push(group);
        }
      }
      
      if (groups.length === 0) {
        toast({
          title: "No Duplicates Found",
          description: "All your contacts appear to be unique!",
        });
        setIsProcessingDuplicates(false);
        return;
      }
      
      // Initialize selection (first contact in each group by default)
      const initialSelection = {};
      groups.forEach((group, idx) => {
        initialSelection[idx] = group[0]._id;
      });
      
      setDuplicateGroups(groups);
      setSelectedToKeep(initialSelection);
      setShowDuplicateManager(true);
      setIsProcessingDuplicates(false);
      
      toast({
        title: "Duplicates Found",
        description: `Found ${groups.length} group(s) of duplicate contacts`,
      });
    } catch (error) {
      console.error('Error finding duplicates:', error);
      toast({
        title: "Error",
        description: "Failed to find duplicates. Please try again.",
        variant: "destructive"
      });
      setIsProcessingDuplicates(false);
    }
  };
  
  const areDuplicates = (contact1, contact2) => {
    const normalize = (str) => (str || '').toLowerCase().trim();
    
    const name1 = normalize(contact1.name);
    const name2 = normalize(contact2.name);
    const email1 = normalize(contact1.email);
    const email2 = normalize(contact2.email);
    const phone1 = normalize(contact1.phone);
    const phone2 = normalize(contact2.phone);
    const company1 = normalize(contact1.company);
    const company2 = normalize(contact2.company);
    
    // Match if name and (email or phone or company) match
    if (name1 && name2 && name1 === name2) {
      if ((email1 && email2 && email1 === email2) ||
          (phone1 && phone2 && phone1 === phone2) ||
          (company1 && company2 && company1 === company2)) {
        return true;
      }
    }
    
    // Also match if email and phone both match (even without name)
    if (email1 && email2 && email1 === email2 && phone1 && phone2 && phone1 === phone2) {
      return true;
    }
    
    return false;
  };
  
  const handleRemoveDuplicates = async () => {
    try {
      setIsProcessingDuplicates(true);
      
      const { cachedScannedContactAPI } = await import('../services/cachedApi');
      
      let deletedCount = 0;
      
      // For each group, delete all except the selected one
      for (let groupIdx = 0; groupIdx < duplicateGroups.length; groupIdx++) {
        const group = duplicateGroups[groupIdx];
        const keepId = selectedToKeep[groupIdx];
        
        for (const contact of group) {
          if (contact._id !== keepId) {
            await cachedScannedContactAPI.delete(contact._id, user.uid);
            deletedCount++;
          }
        }
      }
      
      // Close dialog
      setShowDuplicateManager(false);
      setDuplicateGroups([]);
      setSelectedToKeep({});
      setIsProcessingDuplicates(false);
      
      // Reload contacts
      await loadScannedContacts(true);
      
      toast({
        title: "Duplicates Removed",
        description: `Successfully deleted ${deletedCount} duplicate contact(s)`,
      });
    } catch (error) {
      console.error('Error removing duplicates:', error);
      toast({
        title: "Error",
        description: "Failed to remove some duplicates. Please try again.",
        variant: "destructive"
      });
      setIsProcessingDuplicates(false);
    }
  };

  const resetScanState = () => {
    setFrontImage(null);
    setBackImage(null);
    setFrontPreview(null);
    setBackPreview(null);
    setShowCropModal(false);
    setCroppedFrontImage(null);
    setCroppedBackImage(null);
    setIsProcessing(false);
  };

  // Scanner functions
  const handleFrontImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Open crop modal
        setCropImageSrc(reader.result);
        setCropImageType('front');
        setZoom(1.5); // Reset zoom to 1.5x for better initial coverage
        setCrop({ x: 0, y: 0 }); // Reset crop position
        setShowCropModal(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBackImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Open crop modal
        setCropImageSrc(reader.result);
        setCropImageType('back');
        setZoom(1.5); // Reset zoom to 1.5x for better initial coverage
        setCrop({ x: 0, y: 0 }); // Reset crop position
        setShowCropModal(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const processCard = async () => {
    if (!frontImage) {
      toast({
        title: "Image Required",
        description: "Please upload at least the front image of the card.",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsProcessing(true);
      
      // Create FormData with images
      const formData = new FormData();
      formData.append('frontImage', frontImage);
      if (backImage) {
        formData.append('backImage', backImage);
      }
      
      // Send to backend - creates placeholder and processes in background
      const { cachedScannedContactAPI } = await import('../services/cachedApi');
      const result = await cachedScannedContactAPI.processAndSave(formData, user.uid);
      
      console.log('Backend response:', result);
      
      // Note: Duplicate detection happens after OCR processing completes (in polling)
      // The card is submitted with status='processing', OCR extraction happens async
      
      // Close modal and reset state AFTER API call (prevents blinking)
      setShowScanModal(false);
      setFrontImage(null);
      setBackImage(null);
      setFrontPreview(null);
      setBackPreview(null);
      setShowCropModal(false);
      setCroppedFrontImage(null);
      setCroppedBackImage(null);
      setIsProcessing(false);
      
      // Reload the contacts list to show the newly added card
      // The backend has already created a placeholder with status='processing'
      await loadScannedContacts(true); // Force refresh
      
      console.log('✅ Card submitted for processing. Polling will check for duplicates after OCR completes.');
      
      toast({
        title: "Processing Started",
        description: "Your card is being processed. We'll check for duplicates once extraction completes.",
        duration: 5000,
      });
      
    } catch (error) {
      console.error('Error processing card:', error);
      
      // Check if it's a scan limit error
      if (error.response?.status === 403 && error.response?.data?.detail?.message === "Scan limit reached") {
        const limitData = error.response.data.detail;
        setScanLimitDialog({
          open: true,
          scansUsed: limitData.scans_used,
          maxScans: limitData.max_scans,
          contactEmail: limitData.contact_email
        });
        setCanScan(false);
        setScansRemaining(limitData.scans_remaining || 0);
        setShowScanModal(false);
        setIsProcessing(false);
        return;
      }
      
      let errorMessage = "Failed to process card image. Please try again.";
      
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : error.response.data.detail.message || errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setIsProcessing(false);
      
      toast({
        title: "Processing Failed",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleScanClick = () => {
    if (canScan) {
      setShowScanModal(true);
    } else {
      setScanLimitDialog(prev => ({ ...prev, open: true }));
    }
  };

  const handleManualContactSave = async () => {
    // Validate required fields
    if (!manualContactData.name.trim()) {
      toast({
        title: "Missing Information",
        description: "Please enter a name for the contact.",
        variant: "destructive"
      });
      return;
    }

    setSavingManualContact(true);
    try {
      const { scannedContactAPI } = await import('../services/api');
      
      // Create FormData for API submission (backend expects multipart/form-data)
      const formData = new FormData();
      formData.append('name', manualContactData.name);
      formData.append('title', manualContactData.title || '');
      formData.append('company', manualContactData.company || '');
      formData.append('email', manualContactData.email || '');
      formData.append('phone', manualContactData.phone || '');
      formData.append('websiteName', manualContactData.websiteName || '');
      formData.append('websiteTLD', manualContactData.websiteTLD || '');
      formData.append('location', manualContactData.location || '');
      formData.append('locationAddress', manualContactData.locationAddress || '');
      formData.append('bio', manualContactData.bio || '');
      formData.append('linkedin', manualContactData.linkedin || '');
      formData.append('twitter', manualContactData.twitter || '');
      formData.append('github', manualContactData.github || '');
      formData.append('instagram', manualContactData.instagram || '');
      formData.append('youtube', manualContactData.youtube || '');
      formData.append('facebook', manualContactData.facebook || '');
      formData.append('whatsapp', manualContactData.whatsapp || '');
      // Note: frontImage and backImage are optional files, we're not adding them for manual entry

      // Save directly without OCR processing
      const result = await scannedContactAPI.create(formData);

      toast({
        title: "Contact Created",
        description: "Contact has been added successfully.",
      });

      // Trigger update event for other components (Dashboard, Home)
      localStorage.setItem('contact_updated', Date.now().toString());
      window.dispatchEvent(new Event('storage'));

      // Refresh contacts list and groups
      await loadScannedContacts(true);
      await loadGroups();

      // Reset and close dialog
      setManualContactData({
        name: '',
        title: '',
        company: '',
        email: '',
        phone: '',
        websiteName: '',
        websiteTLD: '.com',
        location: '',
        locationAddress: '',
        bio: '',
        linkedin: '',
        twitter: '',
        github: '',
        instagram: '',
        youtube: '',
        facebook: '',
        whatsapp: '',
        notes: ''
      });
      setShowManualContactDialog(false);

    } catch (error) {
      console.error('Error saving manual contact:', error);
      toast({
        title: "Save Failed",
        description: error.response?.data?.detail || "Failed to save contact. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSavingManualContact(false);
    }
  };

  const closeScanModal = () => {
    setShowScanModal(false);
    setFrontImage(null);
    setBackImage(null);
    setFrontPreview(null);
    setBackPreview(null);
    setExtractedData(null);
    setFormData({
      name: '',
      title: '',
      company: '',
      email: '',
      phone: '',
      websiteName: '',
      websiteTLD: '',
      location: '',
      locationAddress: '',
      bio: '',
      linkedin: '',
      twitter: '',
      github: '',
      instagram: '',
      youtube: '',
      facebook: '',
      whatsapp: ''
    });
  };

  const deleteScannedContact = (contactId, contactName) => {
    openDeleteDialog('scanned', { id: contactId, _id: contactId }, contactName);
  };

  // Merge digital and scanned contacts into a unified array with type indicators
  const allContacts = React.useMemo(() => {
    const digitalWithType = contacts.map(contact => ({
      ...contact,
      contactType: 'digital',
      // Use username as unique identifier for display
      displayId: contact.username || contact.id || contact._id,
      // Store the actual database ID for deletion (for old contacts)
      dbId: contact.id || contact._id
    }));
    
    const scannedWithType = scannedContacts.map(contact => ({
      ...contact,
      contactType: 'scanned',
      displayId: contact._id,
      dbId: contact._id
    }));
    
    return [...digitalWithType, ...scannedWithType];
  }, [contacts, scannedContacts]);
  
  // Filter unified contacts by search query and group
  const filteredAllContacts = React.useMemo(() => {
    const query = searchQuery.toLowerCase();
    
    // First apply search filter
    const searchFiltered = allContacts.filter(contact => {
      return (
        (contact.name && contact.name.toLowerCase().includes(query)) ||
        (contact.email && contact.email.toLowerCase().includes(query)) ||
        (contact.company && contact.company.toLowerCase().includes(query)) ||
        (contact.title && contact.title.toLowerCase().includes(query)) ||
        (contact.phone && contact.phone.toLowerCase().includes(query)) ||
        (contact.location && contact.location.toLowerCase().includes(query)) ||
        (contact.bio && contact.bio.toLowerCase().includes(query)) ||
        (contact.notes && contact.notes.toLowerCase().includes(query)) ||
        (contact.website && contact.website.toLowerCase().includes(query)) ||
        (contact.websiteName && contact.websiteName.toLowerCase().includes(query)) ||
        (contact.linkedin && contact.linkedin.toLowerCase().includes(query)) ||
        (contact.twitter && contact.twitter.toLowerCase().includes(query)) ||
        (contact.github && contact.github.toLowerCase().includes(query)) ||
        (contact.instagram && contact.instagram.toLowerCase().includes(query))
      );
    });
    
    // Then apply group filter (for both digital and scanned contacts)
    const groupFiltered = selectedGroup === 'all'
      ? searchFiltered
      : searchFiltered.filter(contact => 
          contact.group_ids && contact.group_ids.includes(selectedGroup)
        );
    
    // Sort: Digital contacts first, then scanned contacts, within each type sort by notes count
    return groupFiltered.sort((a, b) => {
      // First, sort by type (digital before scanned)
      if (a.contactType !== b.contactType) {
        return a.contactType === 'digital' ? -1 : 1;
      }
      
      // Within same type, sort by notes count (descending)
      const notesCountA = a.notes ? a.notes.split('\n').filter(line => line.trim()).length : 0;
      const notesCountB = b.notes ? b.notes.split('\n').filter(line => line.trim()).length : 0;
      return notesCountB - notesCountA;
    });
  }, [allContacts, searchQuery, selectedGroup]);
  
  // Keep legacy filtered arrays for backward compatibility (empty or minimal use)
  const filteredContacts = [];
  const filteredScannedContacts = [];
  
  // Search handler with debounce
  const handleSearchChange = (e) => {
    const newSearch = e.target.value;
    setSearchQuery(newSearch);
    setCurrentPage(1); // Reset to first page when searching
  };
  
  // Pagination handlers
  const handlePageChange = async (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setPaginationLoading(true);
      setCurrentPage(newPage);
      // Scroll to scanned contacts section
      const scannedSection = document.querySelector('.unified-contacts-section');
      if (scannedSection) {
        scannedSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      // Loading will be cleared when loadScannedContacts completes via useEffect
    }
  };
  
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };
  
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };
  
  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      // Show all pages if total is less than max
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show subset with current page in middle
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, currentPage + 2);
      
      // Adjust if at start or end
      if (currentPage <= 3) {
        endPage = maxPagesToShow;
      } else if (currentPage >= totalPages - 2) {
        startPage = totalPages - maxPagesToShow + 1;
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  const exportToCSV = () => {
    // Filter out any contacts that might have deleted flag
    const activeContacts = (contacts || []).filter(c => !c.deleted && !c.isDeleted);
    const activeScannedContacts = (scannedContacts || []).filter(c => !c.deleted && !c.isDeleted);
    
    const totalContacts = activeContacts.length + activeScannedContacts.length;
    
    if (totalContacts === 0) {
      toast({
        title: "No Contacts",
        description: "You don't have any contacts to export.",
        variant: "destructive"
      });
      return;
    }

    // Define CSV headers
    const headers = ['Type', 'Name', 'Email', 'Mobile No.', 'Office No.', 'Company', 'Title', 'Bio', 'Location', 'Address', 'Website', 'Notes', 'LinkedIn', 'Twitter', 'Instagram', 'GitHub', 'YouTube', 'Facebook', 'WhatsApp'];
    
    // Convert digital contacts to CSV rows (only active ones)
    const digitalRows = activeContacts.map(contact => {
      return [
        'Digital',
        contact.name || '',
        contact.email || '',
        contact.phone || '',
        '', // office no. (digital contacts don't have this)
        contact.company || '',
        contact.title || '',
        contact.bio || '',
        contact.location || '',
        '', // address (digital contacts don't have this)
        contact.website || '',
        contact.notes || '',
        contact.linkedin || '',
        contact.twitter || '',
        contact.instagram || '',
        contact.github || '',
        contact.youtube || '',
        contact.facebook || '',
        '' // whatsapp
      ].map(field => {
        // Escape quotes and wrap in quotes if contains comma or newline
        const escaped = String(field).replace(/"/g, '""');
        return escaped.includes(',') || escaped.includes('\n') ? `"${escaped}"` : escaped;
      }).join(',');
    });

    // Convert scanned contacts to CSV rows (only active ones)
    const scannedRows = activeScannedContacts.map(contact => {
      const website = contact.websiteName ? `${contact.websiteName}${contact.websiteTLD?.startsWith('.') ? '' : '.'}${contact.websiteTLD || ''}` : '';
      return [
        'Physical Card',
        contact.name || '',
        contact.email || '',
        contact.phone || '',
        contact.phone2 || '',
        contact.company || '',
        contact.title || '',
        contact.bio || '',
        contact.location || '',
        contact.locationAddress || '',
        website,
        contact.notes || '',
        contact.linkedin || '',
        contact.twitter || '',
        contact.instagram || '',
        contact.github || '',
        contact.youtube || '',
        contact.facebook || '',
        contact.whatsapp || ''
      ].map(field => {
        // Escape quotes and wrap in quotes if contains comma or newline
        const escaped = String(field).replace(/"/g, '""');
        return escaped.includes(',') || escaped.includes('\n') ? `"${escaped}"` : escaped;
      }).join(',');
    });

    // Combine all rows
    const allRows = [...digitalRows, ...scannedRows];

    // Combine headers and rows
    const csvContent = [headers.join(','), ...allRows].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `contacts_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Export Successful!",
      description: `Exported ${totalContacts} contact${totalContacts !== 1 ? 's' : ''} to CSV (${activeContacts.length} digital, ${activeScannedContacts.length} physical).`
    });
  };

  // Import contacts handlers
  const handleImportClick = () => {
    setShowImportDialog(true);
    setImportFile(null);
    setImportStatus(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['.csv', '.xlsx', '.xls'];
      const fileExt = '.' + file.name.split('.').pop().toLowerCase();
      if (!validTypes.includes(fileExt)) {
        toast({
          title: "Invalid File Type",
          description: "Please select a CSV or Excel file (.csv, .xlsx, .xls)",
          variant: "destructive"
        });
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "Maximum file size is 5MB",
          variant: "destructive"
        });
        return;
      }
      
      setImportFile(file);
    }
  };

  const handleImportSubmit = async () => {
    if (!importFile) {
      toast({
        title: "No File Selected",
        description: "Please select a file to import",
        variant: "destructive"
      });
      return;
    }

    setIsImporting(true);
    setImportStatus({ status: 'uploading', message: 'Uploading file...' });

    try {
      const result = await contactAPI.importContacts(importFile);
      
      setImportStatus({
        status: 'processing',
        message: result.message || 'Import started. Contacts are being processed...'
      });

      toast({
        title: "Import Started",
        description: "Your contacts are being imported in the background."
      });

      // Poll for status updates
      let attempts = 0;
      const maxAttempts = 30; // 30 seconds max
      const pollInterval = setInterval(async () => {
        attempts++;
        try {
          const status = await contactAPI.getImportStatus();
          
          if (status.status === 'completed') {
            clearInterval(pollInterval);
            setImportStatus({
              status: 'completed',
              message: `Successfully imported ${status.contacts_created || 0} contacts!`,
              details: status
            });
            setIsImporting(false);
            
            // Refresh contacts list
            loadScannedContacts(true);
            
            toast({
              title: "Import Complete!",
              description: `${status.contacts_created || 0} contacts imported successfully.`
            });
          } else if (status.status === 'failed') {
            clearInterval(pollInterval);
            setImportStatus({
              status: 'failed',
              message: status.error || 'Import failed. Please try again.',
              details: status
            });
            setIsImporting(false);
            
            toast({
              title: "Import Failed",
              description: status.error || "There was an error importing your contacts.",
              variant: "destructive"
            });
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            setImportStatus({
              status: 'completed',
              message: 'Import is still processing. Please refresh the page in a moment.'
            });
            setIsImporting(false);
          }
        } catch (error) {
          console.error('Error polling import status:', error);
        }
      }, 1000);

    } catch (error) {
      console.error('Import error:', error);
      setIsImporting(false);
      setImportStatus({
        status: 'failed',
        message: error.response?.data?.detail || 'Failed to upload file. Please try again.'
      });
      
      toast({
        title: "Import Failed",
        description: error.response?.data?.detail || "There was an error uploading your file.",
        variant: "destructive"
      });
    }
  };

  // Generate structured data
  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      getBreadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: 'My Contacts', path: '/contacts' }
      ])
    ]
  };

  return (
    <Layout userCard={userCard} loading={cardLoading}>
      <SEO 
        title="All Contacts"
        description="Manage all your contacts - digital and scanned physical business cards. View, organize, and add notes to your contacts."
        keywords="contacts, digital contacts, scanned cards, physical business cards, card scanner, OCR business cards, manage contacts"
        canonicalUrl={generateCanonicalUrl('/contacts')}
        structuredData={structuredData}
        noindex={true}
      />
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen py-4 sm:py-6">
        <div className="max-w-6xl mx-auto px-2 sm:px-4 lg:px-6">
          {/* Page Header - CRM Style */}
          <div className="bg-white shadow-sm rounded-lg mb-4">
            <div className="px-3 sm:px-4 py-2 sm:py-3">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-base sm:text-lg font-bold text-gray-900">Contacts</h1>
                  <p className="text-xs text-gray-600">
                    {contacts.length} digital • {totalScannedContacts} card contacts
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleImportClick}
                    variant="outline"
                    size="sm"
                    className="border-gray-300 text-gray-700 hover:bg-gray-50 h-8 text-xs"
                  >
                    <FileDown className="w-3.5 h-3.5 mr-1.5" />
                    Import
                  </Button>
                  
                  {(contacts.length > 0 || scannedContacts.length > 0) && (
                    <Button
                      onClick={exportToCSV}
                      variant="outline"
                      size="sm"
                      className="border-gray-300 text-gray-700 hover:bg-gray-50 h-8 text-xs"
                    >
                      <FileUp className="w-3.5 h-3.5 mr-1.5" />
                      Export
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Scan Usage Banner - Simplified */}
          {scansRemaining !== null && scansRemaining <= 3 && (
            <div className={`mb-6 p-4 rounded-lg border ${
              scansRemaining === 0 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Scan className={`w-5 h-5 ${scansRemaining === 0 ? 'text-red-600' : 'text-amber-600'}`} />
                  <div>
                    <p className={`font-medium text-sm ${scansRemaining === 0 ? 'text-red-900' : 'text-amber-900'}`}>
                      {scansRemaining === 0 ? 'No scans remaining' : `${scansRemaining} scan${scansRemaining !== 1 ? 's' : ''} remaining`}
                    </p>
                  </div>
                </div>
                {scansRemaining === 0 && (
                  <Button
                    onClick={() => setScanLimitDialog(prev => ({ ...prev, open: true }))}
                    size="sm"
                    className="bg-amber-600 hover:bg-amber-700 text-white h-8"
                  >
                    Get More
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Search and Filters */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div>
              {/* Pagination - Only for Card Contacts tab */}
              {activeTab === 'scanned' && totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1 || paginationLoading}
                    variant="outline"
                    size="sm"
                    className="h-8"
                  >
                    Previous
                  </Button>
                  
                  {getPageNumbers().map((pageNum) => (
                    <Button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      variant={currentPage === pageNum ? "default" : "outline"}
                      size="sm"
                      disabled={paginationLoading}
                      className={`h-8 ${currentPage === pageNum ? "bg-gray-900 text-white" : ""}`}
                    >
                      {pageNum}
                    </Button>
                  ))}
                  
                  <Button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages || paginationLoading}
                    variant="outline"
                    size="sm"
                    className="h-8"
                  >
                    Next
                  </Button>
                  
                  {paginationLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                </div>
              )}
              </div>
            </div>
            
            {/* Combined Search and Actions Bar */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center gap-4 flex-wrap">
                {/* Search */}
                <div className="flex-1 min-w-[300px] relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Search by name, email, or company..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="pl-10 border-gray-300"
                  />
                </div>
                
                {/* Creator Filter (Owner only) */}
                {team && team.my_role === 'owner' && teamMembers.length > 0 && (
                  <div className="relative creator-filter-dropdown">
                    <Button
                      onClick={() => setShowCreatorFilterDropdown(!showCreatorFilterDropdown)}
                      size="sm"
                      variant="outline"
                      className={`${creatorFilter.length > 0 ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-300'}`}
                    >
                      <Users className="w-4 h-4 mr-1" />
                      Created By
                      {creatorFilter.length > 0 && (
                        <span className="ml-1 px-1.5 py-0.5 bg-amber-600 text-white text-xs rounded-full">
                          {creatorFilter.length}
                        </span>
                      )}
                      <ChevronDown className="w-4 h-4 ml-1" />
                    </Button>
                    
                    {showCreatorFilterDropdown && (
                      <div className="absolute top-full right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                        <div className="p-3 border-b border-gray-200">
                          <p className="text-sm font-semibold text-gray-700">Filter by Creator</p>
                        </div>
                        <div className="p-2 max-h-64 overflow-y-auto">
                          {/* All option */}
                          <label
                            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded cursor-pointer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={creatorFilter.length === 0}
                              onChange={() => {
                                setCreatorFilter([]);
                                setCurrentPage(1);
                              }}
                              className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                            />
                            <span className="text-sm text-gray-700 font-medium">All Contacts</span>
                          </label>
                          
                          {/* Self option */}
                          <label
                            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded cursor-pointer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={creatorFilter.includes(userCard?.userId)}
                              onChange={() => {
                                setCreatorFilter(prev => 
                                  prev.includes(userCard?.userId)
                                    ? prev.filter(id => id !== userCard?.userId)
                                    : [...prev.filter(id => id !== undefined), userCard?.userId]
                                );
                                setCurrentPage(1);
                              }}
                              className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                            />
                            <span className="text-sm text-gray-700">Created by Me</span>
                          </label>
                          
                          {/* Divider */}
                          <div className="my-2 border-t border-gray-200"></div>
                          
                          {/* Team members */}
                          <p className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase">Team Members</p>
                          {teamMembers
                            .filter(member => member.user_id !== userCard?.userId)
                            .map((member) => (
                              <label
                                key={member.user_id}
                                className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded cursor-pointer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  checked={creatorFilter.includes(member.user_id)}
                                  onChange={() => {
                                    setCreatorFilter(prev => 
                                      prev.includes(member.user_id)
                                        ? prev.filter(id => id !== member.user_id)
                                        : [...prev, member.user_id]
                                    );
                                    setCurrentPage(1);
                                  }}
                                  className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-700 truncate">{member.email}</p>
                                  <p className="text-xs text-gray-500 capitalize">{member.role}</p>
                                </div>
                              </label>
                            ))}
                        </div>
                        <div className="p-3 border-t border-gray-200 flex justify-between">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setCreatorFilter([]);
                              setCurrentPage(1);
                            }}
                          >
                            Clear
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setShowCreatorFilterDropdown(false)}
                          >
                            Done
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Quick Actions */}
                <div className="flex items-center gap-2">
                  {totalScannedContacts >= 2 && (
                    <Button
                      onClick={findAllDuplicates}
                      disabled={isProcessingDuplicates}
                      size="sm"
                      variant="outline"
                      className="h-8"
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Find Duplicates
                    </Button>
                  )}
                  
                  <Button
                    onClick={() => {
                      setEditingGroup(null);
                      setGroupFormData({ name: '', color: '#3B82F6', icon: 'folder' });
                      setShowGroupModal(true);
                    }}
                    size="sm"
                    variant="outline"
                    className="h-8"
                  >
                    <FolderPlus className="w-4 h-4 mr-1" />
                    New Group
                  </Button>
                  
                  <Button
                    onClick={() => setShowManualContactDialog(true)}
                    size="sm"
                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    Create Contact
                  </Button>
                </div>
              </div>
              
              {/* Group Filter Tabs - Standard Blue Theme */}
              {groups.length > 0 && (
                <div className="flex items-center gap-2 overflow-x-auto mt-4 pt-4 border-t border-gray-200 pb-2">
                  {/* All Tab */}
                  <button
                    onClick={() => setSelectedGroup('all')}
                    className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap border-2 ${
                      selectedGroup === 'all'
                        ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                    }`}
                  >
                    All ({allContacts.length})
                  </button>

                  {/* Group Tabs */}
                  {groups.map((group) => {
                    // Calculate actual count from both digital and scanned contacts
                    const groupContactCount = allContacts.filter(contact => 
                      contact.group_ids && contact.group_ids.includes(group.id)
                    ).length;
                    
                    return (
                    <button
                      key={group.id}
                      onClick={() => setSelectedGroup(group.id)}
                      className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap border-2 ${
                        selectedGroup === group.id
                          ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                      }`}
                    >
                      {group.name} ({groupContactCount})
                    </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

        {loading ? (
          <Card className="border-2 border-gray-100 p-12 text-center">
            <div className="inline-block mb-4">
              <div className="w-16 h-16 border-4 border-amber-200 border-t-amber-600 rounded-full animate-spin"></div>
            </div>
            <p className="text-gray-600 text-lg">Loading contacts...</p>
          </Card>
        ) : filteredAllContacts.length === 0 ? (
          <Card className="border-2 border-gray-100 p-12 text-center">
            <UserPlus className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">
              {searchQuery ? 'No contacts found matching your search.' : "You haven't saved any contacts yet."}
            </p>
            {!searchQuery && (
              <p className="text-sm text-gray-400 mb-4">
                Connect with professionals by scanning their QR codes or viewing their cards
              </p>
            )}
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Contact Type Tabs - Professional */}
            <div className="flex border-b border-gray-200 bg-white rounded-t-lg">
              <button
                onClick={() => setActiveTab('scanned')}
                className={`flex items-center gap-2 px-5 py-3 font-medium text-sm transition-colors border-b-2 -mb-[2px] ${
                  activeTab === 'scanned' 
                    ? 'border-gray-900 text-gray-900' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <CreditCard className="w-4 h-4" />
                Card Contacts
                <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs">
                  {totalScannedContacts}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('digital')}
                className={`flex items-center gap-2 px-5 py-3 font-medium text-sm transition-colors border-b-2 -mb-[2px] ${
                  activeTab === 'digital' 
                    ? 'border-gray-900 text-gray-900' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Smartphone className="w-4 h-4" />
                Digital Contacts
                <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs">
                  {contacts.length}
                </span>
              </button>
            </div>

            {/* Contacts Section - Filtered by Tab */}
            {filteredAllContacts.filter(c => 
              activeTab === 'scanned' ? c.contactType === 'scanned' : c.contactType === 'digital'
            ).length > 0 && (
              <div className="unified-contacts-section relative">
                {/* Loading overlay for pagination - only for Card Contacts tab */}
                {activeTab === 'scanned' && paginationLoading && (
                  <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center rounded-lg">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="text-sm">Loading...</span>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {filteredAllContacts.filter(c => 
                    activeTab === 'scanned' ? c.contactType === 'scanned' : c.contactType === 'digital'
                  ).map((contact) => {
                    const contactId = contact.displayId || contact._id || contact.id;
                    const notesLineCount = contact.notes ? contact.notes.split('\n').filter(line => line.trim()).length : 0;
                    const websiteUrl = contact.websiteName && contact.websiteTLD
                      ? `https://${contact.websiteName}${contact.websiteTLD?.startsWith('.') ? '' : '.'}${contact.websiteTLD}` 
                      : contact.website;
                    const isDigital = contact.contactType === 'digital';
                    const isScanned = contact.contactType === 'scanned';
                    
                    // Determine navigation path based on contact type
                    const handleContactClick = () => {
                      if (contact.isProcessing) return;
                      if (isDigital) {
                        navigate(`/bio/${contact.username}`);
                      } else {
                        navigate(`/contact/${contactId}`);
                      }
                    };
                    
                    return (
                      <div key={contactId} className="relative">
                        {/* Contact Card - Unified Display for Both Types */}
                        <div 
                          className={`w-full cursor-pointer transition-transform duration-200 ${contact.isProcessing ? 'cursor-default' : 'hover:scale-[1.02]'}`}
                          onClick={handleContactClick}
                        >
                          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden relative border-2 border-gray-200">
                            {/* Type Badge - Only show for Digital contacts */}
                            {isDigital && (
                              <div className="absolute top-4 right-4 bg-gradient-to-r from-amber-600 to-yellow-500 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-lg flex items-center gap-1 z-20">
                                <Globe className="w-3 h-3" />
                                Digital
                              </div>
                            )}
                            
                            {/* Processing Badge */}
                            {contact.isProcessing && (
                              <div className="absolute top-0 left-0 right-0 bg-blue-500 text-white text-center py-1 text-xs font-semibold z-10">
                                Processing - Extracting contact information...
                              </div>
                            )}
                            
                            <div className={`relative p-3 sm:p-6 md:p-8 ${contact.isProcessing ? 'pt-8 sm:pt-10' : ''}`}>
                              <div className="flex flex-row gap-3 sm:gap-6 md:gap-8">
                                {/* Left: Profile Photo for Digital / Card Images for Scanned */}
                                {!contact.isProcessing && (
                                  <div className="flex-shrink-0">
                                    {isDigital ? (
                                      /* Profile Photo for Digital Contacts - Smaller Size */
                                      <div className="space-y-2">
                                        {contact.profileImage ? (
                                          <img 
                                            src={contact.profileImage}
                                            alt={contact.name} 
                                            className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 object-cover rounded-full border-3 border-amber-300 shadow-lg"
                                          />
                                        ) : (
                                          <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full border-3 border-amber-300 shadow-lg bg-gradient-to-br from-amber-400 to-yellow-400 flex items-center justify-center">
                                            <span className="text-white text-xl sm:text-2xl md:text-3xl font-bold">
                                              {contact.name ? contact.name.charAt(0).toUpperCase() : '?'}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      /* Card Images for Scanned Contacts */
                                      (contact.frontImage || contact.backImage || contact.frontImageUrl) && (
                                        <div className="space-y-2">
                                          {(contact.frontImage || contact.frontImageUrl) && (
                                            <img 
                                              src={contact.frontImage || contact.frontImageUrl} 
                                              alt="Front" 
                                              className="w-20 h-auto sm:w-28 sm:h-auto md:w-36 md:h-auto object-contain rounded-lg border-2 border-gray-300 shadow-md"
                                            />
                                          )}
                                          {contact.backImage && (
                                            <img 
                                              src={contact.backImage} 
                                              alt="Back" 
                                              className="w-20 h-auto sm:w-28 sm:h-auto md:w-36 md:h-auto object-contain rounded-lg border-2 border-gray-300 shadow-md"
                                            />
                                          )}
                                        </div>
                                      )
                                    )}
                                  </div>
                                )}

                                {/* Right: Profile and Info */}
                                <div className="flex-1 flex flex-col justify-center gap-2 sm:gap-3 md:gap-4 min-w-0">
                                  {/* Name, Title, Company */}
                                  <div className="flex-1 min-w-0 space-y-0.5 sm:space-y-1">
                                    <h2 className="text-lg font-bold text-gray-900 leading-tight truncate">
                                      {contact.name}
                                    </h2>
                                    {contact.title && (
                                      <p className="text-sm font-medium text-gray-700 truncate opacity-90">
                                        {contact.title}
                                      </p>
                                    )}
                                    {contact.company && (
                                      <p className="text-sm text-gray-600 truncate opacity-80">
                                        {contact.company}
                                      </p>
                                    )}
                                  </div>

                                  {/* Contact Icons - Email and Phone Only */}
                                  {!contact.isProcessing && (
                                    <div className="flex items-center gap-2 sm:gap-2.5 md:gap-3 justify-between">
                                      <div className="flex items-center gap-3">
                                        {/* Email Icon */}
                                        {contact.email && (
                                          <a href={`mailto:${contact.email}`} onClick={(e) => e.stopPropagation()} className="text-amber-600 hover:text-amber-800 transition-colors p-1 hover:bg-amber-50 rounded-lg" title={contact.email}>
                                            <Mail className="w-5 h-5" />
                                          </a>
                                        )}
                                        {/* Phone Icon */}
                                        {contact.phone && (
                                          <a href={`tel:${contact.phone}`} onClick={(e) => e.stopPropagation()} className="text-teal-600 hover:text-teal-800 transition-colors p-1 hover:bg-teal-50 rounded-lg" title={contact.phone}>
                                            <Phone className="w-5 h-5" />
                                          </a>
                                        )}
                                      </div>

                                      {/* Action Icons - Right Aligned */}
                                      <div className="flex items-center gap-2 sm:gap-2.5 md:gap-3 ml-auto">
                                        {/* Separator */}
                                        <div className="w-px h-5 sm:h-5 md:h-5 bg-gray-300"></div>
                                        
                                        {/* Notes Icon Button */}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openNotesDialog(contact);
                                          }}
                                          className="relative text-amber-600 hover:text-amber-800 transition-colors p-1 hover:bg-amber-50 rounded-lg"
                                          title="Add/Edit Notes"
                                        >
                                          <FileText className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                                          {notesLineCount > 0 && (
                                            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold px-1 rounded-full min-w-[14px] h-[14px] flex items-center justify-center shadow-sm">
                                              {notesLineCount}
                                            </span>
                                          )}
                                        </button>
                                        
                                        {/* Groups Button */}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setContactForGroupAssignment(contact);
                                            setShowGroupSelector(true);
                                          }}
                                          className="text-amber-600 hover:text-amber-800 transition-colors p-1 hover:bg-amber-50 rounded-lg"
                                          title="Assign to Groups"
                                        >
                                          <Tag className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                                        </button>
                                        
                                        {/* Add to CRM Button */}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleAddToCRM(contact);
                                          }}
                                          className="text-teal-600 hover:text-teal-800 transition-colors p-1 hover:bg-teal-50 rounded-lg"
                                          title="Add to CRM"
                                        >
                                          <Handshake className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                                        </button>
                                        
                                        {/* Delete Button */}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            console.log('🔍 Delete button clicked for:', {
                                              contactType: contact.contactType,
                                              username: contact.username,
                                              displayId: contact.displayId,
                                              dbId: contact.dbId,
                                              relationship_id: contact.relationship_id,
                                              name: contact.name,
                                              isDigital: isDigital,
                                              isRelationshipContact: contact.isRelationshipContact
                                            });
                                            if (isDigital) {
                                              openDeleteDialog('digital', {
                                                isRelationshipContact: contact.isRelationshipContact,
                                                username: contact.username,
                                                dbId: contact.dbId,
                                                relationship_id: contact.relationship_id
                                              }, contact.name);
                                            } else {
                                              deleteScannedContact(contactId, contact.name);
                                            }
                                          }}
                                          className="text-red-600 hover:text-red-800 transition-colors p-1 hover:bg-red-50 rounded-lg"
                                          title="Delete Contact"
                                        >
                                          <Trash2 className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5" />
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Pagination Controls - Only for Scanned tab */}
                {activeTab === 'scanned' && totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8">
                    <Button
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1 || paginationLoading}
                      variant="outline"
                      size="sm"
                      className="h-8"
                    >
                      Previous
                    </Button>
                    
                    {getPageNumbers().map((pageNum) => (
                      <Button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        disabled={paginationLoading}
                        className={`h-8 ${currentPage === pageNum ? "bg-gray-900 text-white" : ""}`}
                      >
                        {pageNum}
                      </Button>
                    ))}
                    
                    <Button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages || paginationLoading}
                      variant="outline"
                      size="sm"
                      className="h-8"
                    >
                      Next
                    </Button>
                    
                    {paginationLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                  </div>
                )}
              </div>
            )}
            
            {/* Empty state for current tab */}
            {filteredAllContacts.filter(c => 
              activeTab === 'scanned' ? c.contactType === 'scanned' : c.contactType === 'digital'
            ).length === 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                {activeTab === 'scanned' ? (
                  <>
                    <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-600 mb-1">
                      {searchQuery ? 'No card contacts found' : "No card contacts yet"}
                    </p>
                    {!searchQuery && (
                      <p className="text-sm text-gray-400">
                        Scan business cards or import from CSV/Excel
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <Smartphone className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-600 mb-1">
                      {searchQuery ? 'No digital contacts found' : "No digital contacts yet"}
                    </p>
                    {!searchQuery && (
                      <p className="text-sm text-gray-400">
                        Connect by scanning QR codes
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
        </div>
      </div>

      {/* Crop Modal - Professional Camera Style - Mobile Optimized */}
      <Dialog open={showCropModal} onOpenChange={setShowCropModal}>
        <DialogContent className="w-[95vw] max-w-2xl h-[90vh] bg-gray-900 border-gray-800 p-0 gap-0 flex flex-col">
          <DialogHeader className="px-4 pt-4 pb-3 flex-shrink-0">
            <DialogTitle className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
              <Crop className="w-5 h-5 text-blue-500" />
              Crop Business Card
            </DialogTitle>
            <div className="text-xs sm:text-sm text-gray-300">
              <p className="flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                Drag to reposition • Pinch or zoom to adjust size
              </p>
            </div>
          </DialogHeader>
          
          <div className="relative w-full bg-black flex-1 overflow-hidden" style={{ minHeight: '300px', maxHeight: '500px' }}>
            {cropImageSrc && (
              <Cropper
                image={cropImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1.75}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                showGrid={true}
                objectFit="horizontal-cover"
                restrictPosition={false}
                cropShape="rect"
                style={{
                  containerStyle: {
                    background: '#000'
                  },
                  cropAreaStyle: {
                    border: '3px solid #3B82F6',
                    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
                  }
                }}
              />
            )}
          </div>
          
          <div className="flex-shrink-0 bg-gray-800 px-3 py-3 sm:px-4 sm:py-4 space-y-3 border-t border-gray-700">
            <div className="flex flex-col items-center gap-1 text-xs text-gray-400">
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded-full bg-blue-500"></span>
                Business Card: 3.5&quot; × 2&quot; (1.75:1 ratio)
              </span>
            </div>
            
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-300 mb-2 flex items-center justify-between">
                <span>Zoom</span>
                <span className="text-blue-400 font-bold">{Math.round(zoom * 100)}%</span>
              </label>
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
              />
            </div>
            
            <div className="flex justify-between items-center gap-2 sm:gap-3">
              <button
                onClick={() => {
                  // Skip crop - use full image
                  if (cropImageType === 'front') {
                    fetch(cropImageSrc)
                      .then(res => res.blob())
                      .then(blob => {
                        const file = new File([blob], 'front-full.jpg', { type: 'image/jpeg' });
                        setFrontImage(file);
                        setFrontPreview(cropImageSrc);
                      });
                  } else {
                    fetch(cropImageSrc)
                      .then(res => res.blob())
                      .then(blob => {
                        const file = new File([blob], 'back-full.jpg', { type: 'image/jpeg' });
                        setBackImage(file);
                        setBackPreview(cropImageSrc);
                      });
                  }
                  setShowCropModal(false);
                  setCropImageSrc(null);
                  setCropImageType(null);
                  toast({
                    title: "Full Image Selected",
                    description: "Using complete image without cropping."
                  });
                }}
                className="border-2 border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white px-3 py-3 sm:px-4 sm:py-4 text-xs sm:text-sm font-semibold flex-shrink-0 rounded-lg transition-colors"
              >
                Skip
              </button>
              
              <div className="flex gap-2 flex-1">
                <button
                  onClick={() => {
                    setShowCropModal(false);
                    setCropImageSrc(null);
                    setCropImageType(null);
                  }}
                  className="border-2 border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white px-4 py-3 sm:px-6 sm:py-4 text-xs sm:text-sm font-semibold flex-1 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCropSave}
                  className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white px-4 py-3 sm:px-6 sm:py-4 text-xs sm:text-sm font-semibold shadow-lg shadow-blue-500/50 flex-1 rounded-lg transition-colors"
                >
                  <Crop className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2 inline" />
                  <span className="hidden sm:inline">Apply Crop</span>
                  <span className="sm:hidden">Apply</span>
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Notes Dialog - Redesigned with Blue Theme */}
      <Dialog open={notesDialog.open} onOpenChange={closeNotesDialog}>
        <DialogContent className="sm:max-w-[600px] p-0">
          {/* Header */}
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-100">
            <DialogTitle className="flex items-center gap-3 text-xl font-semibold">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-lg">
                <StickyNote className="w-6 h-6 text-amber-600" strokeWidth={2} />
              </div>
              <span>Notes for {notesDialog.contact?.name}</span>
            </DialogTitle>
          </DialogHeader>
          
          {/* Content */}
          <div className="px-6 py-6">
            <Textarea
              placeholder="Enter your notes here..."
              value={notesDialog.notes}
              onChange={(e) => setNotesDialog(prev => ({ ...prev, notes: e.target.value }))}
              className="min-h-[200px] text-base border-2 border-gray-200 focus:border-amber-400 focus:ring-amber-400 rounded-lg resize-none"
            />
            
            {/* Line Counter */}
            <div className="mt-3">
              <p className="text-sm text-gray-500">
                Lines: {notesDialog.notes.split('\n').length}
              </p>
            </div>
          </div>
          
          {/* Footer with Action Buttons */}
          <div className="px-6 pb-6 flex justify-end gap-3">
            <Button 
              variant="outline" 
              onClick={closeNotesDialog}
              className="px-6 py-2 text-gray-700 border-2 border-gray-300 hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button 
              onClick={saveNotes} 
              className="px-6 py-2 bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600 text-white font-semibold shadow-md hover:shadow-lg transition-all"
            >
              Save Notes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Scanned Contact Dialog */}
      <Dialog open={editScannedDialog.open} onOpenChange={closeEditScannedDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl font-semibold">
              <div className="w-10 h-10 flex items-center justify-center bg-orange-50 rounded-lg">
                <Edit className="w-5 h-5 text-orange-600" />
              </div>
              Edit Scanned Contact
            </DialogTitle>
          </DialogHeader>
          
          {editScannedDialog.contact && (
            <div className="space-y-4 p-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={editScannedDialog.contact.name || ''}
                    onChange={(e) => setEditScannedDialog(prev => ({
                      ...prev,
                      contact: { ...prev.contact, name: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={editScannedDialog.contact.title || ''}
                    onChange={(e) => setEditScannedDialog(prev => ({
                      ...prev,
                      contact: { ...prev.contact, title: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                <input
                  type="text"
                  value={editScannedDialog.contact.company || ''}
                  onChange={(e) => setEditScannedDialog(prev => ({
                    ...prev,
                    contact: { ...prev.contact, company: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={editScannedDialog.contact.email || ''}
                    onChange={(e) => setEditScannedDialog(prev => ({
                      ...prev,
                      contact: { ...prev.contact, email: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                  <input
                    type="tel"
                    value={editScannedDialog.contact.phone || ''}
                    onChange={(e) => setEditScannedDialog(prev => ({
                      ...prev,
                      contact: { ...prev.contact, phone: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                    placeholder="Mobile number"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Office Number</label>
                  <input
                    type="tel"
                    value={editScannedDialog.contact.phone2 || ''}
                    onChange={(e) => setEditScannedDialog(prev => ({
                      ...prev,
                      contact: { ...prev.contact, phone2: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                    placeholder="Office/Landline number"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input
                    type="text"
                    value={editScannedDialog.contact.location || ''}
                    onChange={(e) => setEditScannedDialog(prev => ({
                      ...prev,
                      contact: { ...prev.contact, location: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                    placeholder="City, Country"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input
                  type="text"
                  value={editScannedDialog.contact.locationAddress || ''}
                  onChange={(e) => setEditScannedDialog(prev => ({
                    ...prev,
                    contact: { ...prev.contact, locationAddress: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website Name</label>
                  <input
                    type="text"
                    value={editScannedDialog.contact.websiteName || ''}
                    onChange={(e) => setEditScannedDialog(prev => ({
                      ...prev,
                      contact: { ...prev.contact, websiteName: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                    placeholder="example"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website TLD</label>
                  <input
                    type="text"
                    value={editScannedDialog.contact.websiteTLD || ''}
                    onChange={(e) => setEditScannedDialog(prev => ({
                      ...prev,
                      contact: { ...prev.contact, websiteTLD: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                    placeholder=".com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                <Textarea
                  value={editScannedDialog.contact.bio || ''}
                  onChange={(e) => setEditScannedDialog(prev => ({
                    ...prev,
                    contact: { ...prev.contact, bio: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                  rows={3}
                  placeholder="Professional bio or description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn</label>
                <input
                  type="text"
                  value={editScannedDialog.contact.linkedin || ''}
                  onChange={(e) => setEditScannedDialog(prev => ({
                    ...prev,
                    contact: { ...prev.contact, linkedin: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                  placeholder="username"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Twitter</label>
                  <input
                    type="text"
                    value={editScannedDialog.contact.twitter || ''}
                    onChange={(e) => setEditScannedDialog(prev => ({
                      ...prev,
                      contact: { ...prev.contact, twitter: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                    placeholder="handle"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Instagram</label>
                  <input
                    type="text"
                    value={editScannedDialog.contact.instagram || ''}
                    onChange={(e) => setEditScannedDialog(prev => ({
                      ...prev,
                      contact: { ...prev.contact, instagram: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                    placeholder="handle"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Facebook</label>
                  <input
                    type="text"
                    value={editScannedDialog.contact.facebook || ''}
                    onChange={(e) => setEditScannedDialog(prev => ({
                      ...prev,
                      contact: { ...prev.contact, facebook: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                    placeholder="username"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GitHub</label>
                  <input
                    type="text"
                    value={editScannedDialog.contact.github || ''}
                    onChange={(e) => setEditScannedDialog(prev => ({
                      ...prev,
                      contact: { ...prev.contact, github: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                    placeholder="username"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">YouTube</label>
                  <input
                    type="text"
                    value={editScannedDialog.contact.youtube || ''}
                    onChange={(e) => setEditScannedDialog(prev => ({
                      ...prev,
                      contact: { ...prev.contact, youtube: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                    placeholder="channel"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp</label>
                  <input
                    type="text"
                    value={editScannedDialog.contact.whatsapp || ''}
                    onChange={(e) => setEditScannedDialog(prev => ({
                      ...prev,
                      contact: { ...prev.contact, whatsapp: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-400 focus:ring-orange-400"
                    placeholder="WhatsApp number"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 px-6 pb-6">
            <Button 
              variant="outline" 
              onClick={closeEditScannedDialog}
              className="px-6 py-2"
            >
              Cancel
            </Button>
            <Button 
              onClick={saveScannedContact}
              className="px-6 py-2 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-semibold"
            >
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Scan Card Modal */}
      <Dialog open={showScanModal} onOpenChange={closeScanModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-xl font-bold bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent">
              Scan Document or Photo
            </DialogTitle>
            <p className="text-sm text-gray-600 mt-2">
              Upload business cards, photos, or documents with contact information
            </p>
          </DialogHeader>
          
          {!extractedData ? (
            // Step 1: Upload Images
            <div className="space-y-4 py-2">
              {/* Front Image Upload */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Primary Image <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-2">Upload a business card, photo, or any document containing contact details</p>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-orange-400 transition-colors">
                  {frontPreview ? (
                    <div className="relative">
                      <img src={frontPreview} alt="Primary preview" className="w-full h-48 object-contain rounded" />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setFrontImage(null);
                          setFrontPreview(null);
                        }}
                        className="absolute top-2 right-2 bg-white"
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center space-y-3">
                      <Scan className="w-12 h-12 text-gray-400" />
                      <div className="text-center">
                        <span className="text-sm font-medium text-gray-700 block">Capture or Upload Photo</span>
                        <span className="text-xs text-gray-500">Business card, contact photo, or document</span>
                      </div>
                      <div className="flex gap-3">
                        {/* Camera Button */}
                        <label className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg cursor-pointer hover:from-orange-600 hover:to-pink-600 transition-all">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="text-sm font-medium">Camera</span>
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={handleFrontImageUpload}
                            className="hidden"
                          />
                        </label>
                        
                        {/* Upload Button */}
                        <label className="flex items-center gap-2 px-4 py-2 border-2 border-orange-500 text-orange-600 rounded-lg cursor-pointer hover:bg-orange-50 transition-all">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          <span className="text-sm font-medium">Upload</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFrontImageUpload}
                            className="hidden"
                          />
                        </label>
                      </div>
                      <span className="text-xs text-gray-500">JPEG, PNG (Max 10MB)</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Back Image Upload */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Additional Image <span className="text-gray-400">(Optional)</span>
                </label>
                <p className="text-xs text-gray-500 mb-2">Upload back of card or additional photos with more details</p>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-pink-400 transition-colors">
                  {backPreview ? (
                    <div className="relative">
                      <img src={backPreview} alt="Additional preview" className="w-full h-48 object-contain rounded" />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setBackImage(null);
                          setBackPreview(null);
                        }}
                        className="absolute top-2 right-2 bg-white"
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center space-y-3">
                      <Scan className="w-12 h-12 text-gray-400" />
                      <div className="text-center">
                        <span className="text-sm font-medium text-gray-700 block">Capture or Upload Additional Photo</span>
                        <span className="text-xs text-gray-500">Back of card or supplementary document</span>
                      </div>
                      <div className="flex gap-3">
                        {/* Camera Button */}
                        <label className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-amber-500 text-white rounded-lg cursor-pointer hover:from-pink-600 hover:to-amber-600 transition-all">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="text-sm font-medium">Camera</span>
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={handleBackImageUpload}
                            className="hidden"
                          />
                        </label>
                        
                        {/* Upload Button */}
                        <label className="flex items-center gap-2 px-4 py-2 border-2 border-pink-500 text-pink-600 rounded-lg cursor-pointer hover:bg-pink-50 transition-all">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          <span className="text-sm font-medium">Upload</span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleBackImageUpload}
                            className="hidden"
                          />
                        </label>
                      </div>
                      <span className="text-xs text-gray-500">JPEG, PNG (Max 10MB)</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Process Button */}
              <div className="flex justify-end gap-3">
                <Button 
                  onClick={closeScanModal}
                  variant="outline"
                  className="px-6"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={processCard}
                  disabled={!frontImage || isProcessing}
                  className="bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white px-6"
                >
                  {isProcessing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Extracting Data...
                    </>
                  ) : (
                    <>
                      <Scan className="w-4 h-4 mr-2" />
                      Extract Contact Info
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            // Step 2: Edit Extracted Data
            <div className="space-y-4 py-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-green-800">
                  ✓ Card processed successfully! Please review and edit the information below.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Full Name"
                    className="border-2"
                  />
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Title</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Job Title"
                    className="border-2"
                  />
                </div>

                {/* Company */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Company</label>
                  <Input
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    placeholder="Company Name"
                    className="border-2"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="email@example.com"
                    className="border-2"
                  />
                </div>

                {/* Mobile Number */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Mobile Number</label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+1 234 567 8900"
                    className="border-2"
                  />
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Address</label>
                  <Input
                    value={formData.locationAddress}
                    onChange={(e) => setFormData({...formData, locationAddress: e.target.value})}
                    placeholder="Street Address"
                    className="border-2"
                  />
                </div>
              </div>

              {/* Website */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Website</label>
                <div className="flex gap-2">
                  <Input
                    value={formData.websiteName}
                    onChange={(e) => setFormData({...formData, websiteName: e.target.value})}
                    placeholder="example"
                    className="border-2 flex-1"
                  />
                  <span className="flex items-center text-gray-500">.</span>
                  <Input
                    value={formData.websiteTLD}
                    onChange={(e) => setFormData({...formData, websiteTLD: e.target.value})}
                    placeholder="com"
                    className="border-2 w-24"
                  />
                </div>
              </div>

              {/* Bio */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Notes/Bio</label>
                <Textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({...formData, bio: e.target.value})}
                  placeholder="Additional notes about this contact..."
                  className="border-2 min-h-[80px]"
                />
              </div>

              {/* Social Media - Collapsible Section */}
              {(formData.linkedin || formData.twitter || formData.instagram || formData.facebook) && (
                <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Social Media (Extracted)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {formData.linkedin && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">LinkedIn</label>
                        <Input
                          value={formData.linkedin}
                          onChange={(e) => setFormData({...formData, linkedin: e.target.value})}
                          placeholder="username"
                          className="text-sm"
                        />
                      </div>
                    )}
                    {formData.twitter && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Twitter</label>
                        <Input
                          value={formData.twitter}
                          onChange={(e) => setFormData({...formData, twitter: e.target.value})}
                          placeholder="@username"
                          className="text-sm"
                        />
                      </div>
                    )}
                    {formData.instagram && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Instagram</label>
                        <Input
                          value={formData.instagram}
                          onChange={(e) => setFormData({...formData, instagram: e.target.value})}
                          placeholder="@username"
                          className="text-sm"
                        />
                      </div>
                    )}
                    {formData.facebook && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Facebook</label>
                        <Input
                          value={formData.facebook}
                          onChange={(e) => setFormData({...formData, facebook: e.target.value})}
                          placeholder="username"
                          className="text-sm"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button 
                  onClick={() => {
                    setExtractedData(null);
                    setFormData({
                      name: '',
                      title: '',
                      company: '',
                      email: '',
                      phone: '',
                      websiteName: '',
                      websiteTLD: '',
                      location: '',
                      locationAddress: '',
                      bio: '',
                      linkedin: '',
                      twitter: '',
                      github: '',
                      instagram: '',
                      youtube: '',
                      facebook: '',
                      whatsapp: ''
                    });
                  }}
                  variant="outline"
                  className="px-6"
                >
                  Back
                </Button>
                <Button 
                  onClick={saveScannedContact}
                  disabled={!formData.name}
                  className="bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600 text-white px-6"
                >
                  Save Contact
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialog.open}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        title="Delete Contact"
        message={`Are you sure you want to delete "${deleteDialog.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Scan Limit Dialog */}
      <ScanLimitDialog
        open={scanLimitDialog.open}
        onOpenChange={(open) => setScanLimitDialog(prev => ({ ...prev, open }))}
        scansUsed={scanLimitDialog.scansUsed}
        maxScans={scanLimitDialog.maxScans}
        contactEmail={scanLimitDialog.contactEmail}
      />

      {/* Duplicate Contact Dialog */}
      <DuplicateContactDialog
        open={duplicateDialog.open}
        onClose={() => setDuplicateDialog({ open: false, existingContact: null, newContact: null, newContactData: null })}
        existingContact={duplicateDialog.existingContact}
        newContact={duplicateDialog.newContact}
        onReplace={handleReplaceDuplicate}
        onAddNew={handleAddDuplicateAsNew}
        onIgnore={handleIgnoreDuplicate}
      />

      {/* Bulk Duplicate Manager Dialog */}
      <Dialog open={showDuplicateManager} onOpenChange={setShowDuplicateManager}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              Manage Duplicate Contacts
              <span className="text-base font-normal text-gray-500 ml-2">
                ({duplicateGroups.length} group{duplicateGroups.length !== 1 ? 's' : ''} found)
              </span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            <p className="text-sm text-gray-600">
              Select which contact to keep from each duplicate group. All others will be deleted.
            </p>
            
            {duplicateGroups.map((group, groupIdx) => (
              <div key={groupIdx} className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
                <h3 className="font-semibold text-lg mb-3 text-gray-900">
                  Duplicate Group {groupIdx + 1}
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({group.length} cards)
                  </span>
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {group.map((contact) => {
                    const isSelected = selectedToKeep[groupIdx] === contact._id;
                    const websiteUrl = contact.websiteName && contact.websiteTLD
                      ? `https://${contact.websiteName}${contact.websiteTLD?.startsWith('.') ? '' : '.'}${contact.websiteTLD}` 
                      : contact.website;
                    
                    return (
                      <div
                        key={contact._id}
                        onClick={() => setSelectedToKeep({ ...selectedToKeep, [groupIdx]: contact._id })}
                        className={`relative cursor-pointer transition-all duration-200 rounded-lg border-4 ${
                          isSelected 
                            ? 'border-green-500 shadow-lg scale-105' 
                            : 'border-gray-300 hover:border-blue-400'
                        }`}
                      >
                        {isSelected && (
                          <div className="absolute -top-3 -right-3 z-10 bg-green-500 text-white rounded-full p-2 shadow-lg">
                            <Check className="w-5 h-5" />
                          </div>
                        )}
                        
                        <div className="bg-white rounded-md p-4">
                          {/* Card Images */}
                          {contact.frontImage && (
                            <div className="mb-3">
                              <img 
                                src={contact.frontImage} 
                                alt="Front" 
                                className="w-full h-32 object-contain rounded border border-gray-200"
                              />
                            </div>
                          )}
                          
                          {/* Contact Info */}
                          <div className="space-y-2">
                            <h4 className="font-bold text-lg text-gray-900">{contact.name || 'No Name'}</h4>
                            {contact.title && <p className="text-sm text-gray-600">{contact.title}</p>}
                            {contact.company && <p className="text-sm text-gray-700 font-medium">{contact.company}</p>}
                            {contact.email && (
                              <p className="text-xs text-gray-600 flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {contact.email}
                              </p>
                            )}
                            {contact.phone && (
                              <p className="text-xs text-gray-600 flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {contact.phone}
                              </p>
                            )}
                            {websiteUrl && (
                              <p className="text-xs text-gray-600 flex items-center gap-1">
                                <Globe className="w-3 h-3" />
                                {contact.websiteName || 'Website'}
                              </p>
                            )}
                          </div>
                          
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <p className="text-xs text-gray-400">
                              Created: {new Date(contact.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          
          <div className="flex gap-3 mt-6 pt-4 border-t">
            <Button
              onClick={handleRemoveDuplicates}
              disabled={isProcessingDuplicates}
              className="flex-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white"
            >
              {isProcessingDuplicates ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Removing...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Remove Duplicates ({duplicateGroups.reduce((sum, group) => sum + group.length - 1, 0)} to delete)
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowDuplicateManager(false);
                setDuplicateGroups([]);
                setSelectedToKeep({});
              }}
              disabled={isProcessingDuplicates}
              className="px-6"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Group Management Modal */}
      <Dialog open={showGroupModal} onOpenChange={setShowGroupModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingGroup ? 'Edit Group' : 'Create New Group'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Group Name</label>
              <Input
                value={groupFormData.name}
                onChange={(e) => setGroupFormData({ ...groupFormData, name: e.target.value })}
                placeholder="e.g., Work, Family, Clients"
                className="w-full"
              />
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button
                onClick={saveGroup}
                disabled={!groupFormData.name.trim()}
                className="flex-1 bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600"
              >
                <Check className="w-4 h-4 mr-2" />
                {editingGroup ? 'Update' : 'Create'}
              </Button>
              <Button
                onClick={() => {
                  setShowGroupModal(false);
                  setEditingGroup(null);
                  setGroupFormData({ name: '', color: '#3B82F6', icon: 'folder' });
                }}
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Group Selector Modal */}
      <Dialog open={showGroupSelector} onOpenChange={setShowGroupSelector}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assign to Groups</DialogTitle>
          </DialogHeader>
          {contactForGroupAssignment && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Select groups for <span className="font-semibold">{contactForGroupAssignment.name}</span>
              </p>
              
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {groups.length === 0 ? (
                  <p className="text-sm text-gray-500 italic text-center py-4">
                    No groups yet. Create a group first!
                  </p>
                ) : (
                  groups.map((group) => {
                    const isAssigned = contactForGroupAssignment.group_ids?.includes(group.id) || false;
                    return (
                      <label
                        key={group.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          isAssigned
                            ? 'border-amber-500 bg-amber-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isAssigned}
                          onChange={(e) => {
                            const newGroupIds = e.target.checked
                              ? [...(contactForGroupAssignment.group_ids || []), group.id]
                              : (contactForGroupAssignment.group_ids || []).filter(id => id !== group.id);
                            
                            setContactForGroupAssignment({
                              ...contactForGroupAssignment,
                              group_ids: newGroupIds
                            });
                          }}
                          className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                        />
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: group.color }}
                        >
                          <Tag className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{group.name}</p>
                          <p className="text-xs text-gray-500">{group.contact_count || 0} contacts</p>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  onClick={() => {
                    if (contactForGroupAssignment) {
                      // Use correct ID based on contact type:
                      // - Scanned contacts: use _id (same as displayId)
                      // - Digital contacts: use username (for backend to find in contacts collection)
                      const contactId = contactForGroupAssignment.contactType === 'scanned'
                        ? contactForGroupAssignment._id || contactForGroupAssignment.id
                        : contactForGroupAssignment.username || contactForGroupAssignment.displayId || contactForGroupAssignment._id;
                      
                      console.log('🔍 Assigning contact to groups:', {
                        contactId,
                        contactType: contactForGroupAssignment.contactType,
                        groupIds: contactForGroupAssignment.group_ids || []
                      });
                      
                      assignContactToGroups(
                        contactId,
                        contactForGroupAssignment.group_ids || []
                      );
                    }
                  }}
                  className="flex-1 bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Save
                </Button>
                <Button
                  onClick={() => {
                    setShowGroupSelector(false);
                    setContactForGroupAssignment(null);
                  }}
                  variant="outline"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* CRM Deal Capture Dialog */}
      <Dialog open={showCRMDialog} onOpenChange={setShowCRMDialog}>
        <DialogContent className="max-w-sm sm:max-w-md max-h-[85vh] flex flex-col p-0">
          {/* Header with Close Button */}
          <DialogHeader className="flex flex-row items-center justify-between px-4 py-3 border-b sticky top-0 bg-white z-10 space-y-0">
            <DialogTitle className="text-base font-semibold">Add to CRM</DialogTitle>
            <button
              onClick={() => setShowCRMDialog(false)}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </DialogHeader>

          {/* Scrollable Content */}
          <div className="overflow-y-auto px-4 py-3 space-y-3">
            {/* Contact Name */}
            <div className="text-sm text-gray-600 pb-2 border-b">
              <span className="font-medium">{selectedContactForCRM?.name}</span>
            </div>

            {/* Pipeline Selection */}
            <div>
              <label className="text-xs font-medium text-gray-700 flex items-center gap-1">
                <span>Pipeline *</span>
              </label>
              <select
                value={crmSelectedPipeline}
                onChange={(e) => handlePipelineChange(e.target.value)}
                className="w-full px-2 py-1.5 border rounded-lg text-sm mt-1 bg-white"
              >
                {crmPipelines.map((pipeline) => (
                  <option key={pipeline.id} value={pipeline.id}>
                    {pipeline.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Select which pipeline to add this lead to</p>
            </div>

            {/* Stage Selection */}
            <div>
              <label className="text-xs font-medium text-gray-700">Stage *</label>
              {crmStages.length === 0 ? (
                <div className="mt-1 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div className="flex-1">
                      <p className="text-xs font-medium text-amber-800">No stages in this pipeline</p>
                      <p className="text-xs text-amber-700 mt-1">
                        This pipeline doesn&apos;t have any stages yet. You need to set up stages before adding leads.
                      </p>
                      <button
                        onClick={() => {
                          setShowCRMDialog(false);
                          navigate('/crm');
                        }}
                        className="mt-2 text-xs font-medium text-amber-700 hover:text-amber-800 underline"
                      >
                        Go to CRM to add stages →
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <select
                    value={crmSelectedStage}
                    onChange={(e) => setCrmSelectedStage(e.target.value)}
                    className="w-full px-2 py-1.5 border rounded-lg text-sm mt-1 bg-white"
                  >
                    {crmStages.map((stage) => (
                      <option key={stage.id} value={stage.id}>
                        {stage.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {crmStages.length} stage(s) available in this pipeline
                  </p>
                </>
              )}
            </div>

            {/* Deal Name */}
            <div>
              <label className="text-xs font-medium text-gray-700">Deal Name</label>
              <Input
                value={crmDealName}
                onChange={(e) => setCrmDealName(e.target.value)}
                placeholder="e.g., Website Development"
                className="mt-1 h-8 text-sm"
              />
            </div>

            {/* Deal Amount */}
            <div>
              <label className="text-xs font-medium text-gray-700">Deal Amount (₹)</label>
              <Input
                type="number"
                value={crmDealAmount}
                onChange={(e) => setCrmDealAmount(e.target.value)}
                placeholder="e.g., 50000"
                className="mt-1 h-8 text-sm"
              />
            </div>

            {/* Description */}
            <div>
              <label className="text-xs font-medium text-gray-700">Description</label>
              <Textarea
                value={crmDealDescription}
                onChange={(e) => setCrmDealDescription(e.target.value)}
                placeholder="Enter deal description..."
                rows={2}
                className="mt-1 text-sm"
              />
            </div>

            {/* Footer Buttons */}
            <div className="flex justify-end gap-2 pt-3 border-t">
              <Button 
                variant="outline" 
                onClick={() => setShowCRMDialog(false)}
                size="sm"
                className="h-8"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveToCRM} 
                className="bg-amber-600 hover:bg-amber-700 h-8"
                size="sm"
                disabled={crmStages.length === 0 || !crmSelectedStage}
              >
                Add Lead
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Contact Creation Dialog */}
      <Dialog open={showManualContactDialog} onOpenChange={setShowManualContactDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-green-600" />
              Create Contact Manually
            </DialogTitle>
            <p className="text-sm text-gray-600">Enter contact details without scanning a business card</p>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 px-1">
            <div className="space-y-6 py-4">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-700">Full Name *</label>
                    <Input
                      value={manualContactData.name}
                      onChange={(e) => setManualContactData({...manualContactData, name: e.target.value})}
                      placeholder="John Doe"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700">Job Title</label>
                    <Input
                      value={manualContactData.title}
                      onChange={(e) => setManualContactData({...manualContactData, title: e.target.value})}
                      placeholder="Software Engineer"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700">Company</label>
                    <Input
                      value={manualContactData.company}
                      onChange={(e) => setManualContactData({...manualContactData, company: e.target.value})}
                      placeholder="Tech Corp"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700">Email</label>
                    <Input
                      type="email"
                      value={manualContactData.email}
                      onChange={(e) => setManualContactData({...manualContactData, email: e.target.value})}
                      placeholder="john@example.com"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700">Mobile Number</label>
                    <Input
                      type="tel"
                      value={manualContactData.phone}
                      onChange={(e) => setManualContactData({...manualContactData, phone: e.target.value})}
                      placeholder="+1234567890"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700">Website</label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={manualContactData.websiteName}
                        onChange={(e) => setManualContactData({...manualContactData, websiteName: e.target.value})}
                        placeholder="example"
                        className="flex-1"
                      />
                      <Input
                        value={manualContactData.websiteTLD}
                        onChange={(e) => setManualContactData({...manualContactData, websiteTLD: e.target.value})}
                        placeholder=".com"
                        className="w-24"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Location</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-700">City/Location</label>
                    <Input
                      value={manualContactData.location}
                      onChange={(e) => setManualContactData({...manualContactData, location: e.target.value})}
                      placeholder="San Francisco, CA"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700">Full Address</label>
                    <Input
                      value={manualContactData.locationAddress}
                      onChange={(e) => setManualContactData({...manualContactData, locationAddress: e.target.value})}
                      placeholder="123 Main St, Suite 100"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Social Links</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-700 flex items-center gap-1">
                      <Linkedin className="w-3 h-3" /> LinkedIn
                    </label>
                    <Input
                      value={manualContactData.linkedin}
                      onChange={(e) => setManualContactData({...manualContactData, linkedin: e.target.value})}
                      placeholder="https://linkedin.com/in/johndoe"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700 flex items-center gap-1">
                      <Twitter className="w-3 h-3" /> Twitter
                    </label>
                    <Input
                      value={manualContactData.twitter}
                      onChange={(e) => setManualContactData({...manualContactData, twitter: e.target.value})}
                      placeholder="https://twitter.com/johndoe"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700 flex items-center gap-1">
                      <Instagram className="w-3 h-3" /> Instagram
                    </label>
                    <Input
                      value={manualContactData.instagram}
                      onChange={(e) => setManualContactData({...manualContactData, instagram: e.target.value})}
                      placeholder="https://instagram.com/johndoe"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700 flex items-center gap-1">
                      <Github className="w-3 h-3" /> GitHub
                    </label>
                    <Input
                      value={manualContactData.github}
                      onChange={(e) => setManualContactData({...manualContactData, github: e.target.value})}
                      placeholder="https://github.com/johndoe"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700 flex items-center gap-1">
                      <Youtube className="w-3 h-3" /> YouTube
                    </label>
                    <Input
                      value={manualContactData.youtube}
                      onChange={(e) => setManualContactData({...manualContactData, youtube: e.target.value})}
                      placeholder="https://youtube.com/@johndoe"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700 flex items-center gap-1">
                      <Facebook className="w-3 h-3" /> Facebook
                    </label>
                    <Input
                      value={manualContactData.facebook}
                      onChange={(e) => setManualContactData({...manualContactData, facebook: e.target.value})}
                      placeholder="https://facebook.com/johndoe"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700 flex items-center gap-1">
                      <MessageCircle className="w-3 h-3" /> WhatsApp
                    </label>
                    <Input
                      value={manualContactData.whatsapp}
                      onChange={(e) => setManualContactData({...manualContactData, whatsapp: e.target.value})}
                      placeholder="+1234567890"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Notes & Bio */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Additional Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-gray-700">Bio/Description</label>
                    <Textarea
                      value={manualContactData.bio}
                      onChange={(e) => setManualContactData({...manualContactData, bio: e.target.value})}
                      placeholder="Brief description about the contact..."
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700">Notes</label>
                    <Textarea
                      value={manualContactData.notes}
                      onChange={(e) => setManualContactData({...manualContactData, notes: e.target.value})}
                      placeholder="Add any additional notes..."
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons - Fixed at bottom */}
          <div className="flex justify-end gap-3 pt-4 border-t flex-shrink-0 bg-white">
            <Button
              variant="outline"
              onClick={() => {
                setShowManualContactDialog(false);
                setManualContactData({
                  name: '',
                  title: '',
                  company: '',
                  email: '',
                  phone: '',
                  websiteName: '',
                  websiteTLD: '.com',
                  location: '',
                  locationAddress: '',
                  bio: '',
                  linkedin: '',
                  twitter: '',
                  github: '',
                  instagram: '',
                  youtube: '',
                  facebook: '',
                  whatsapp: '',
                  notes: ''
                });
              }}
              disabled={savingManualContact}
            >
              Cancel
            </Button>
            <Button
              onClick={handleManualContactSave}
              disabled={savingManualContact || !manualContactData.name.trim()}
              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
            >
              {savingManualContact ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create Contact
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Contacts Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileDown className="w-5 h-5 text-gray-700" />
              Import Contacts
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Instructions */}
            <div className="text-sm text-gray-600 space-y-2">
              <p>Upload a CSV or Excel file to import contacts in bulk.</p>
              <div className="bg-gray-50 rounded-lg p-3 text-xs">
                <p className="font-medium text-gray-700 mb-1">Supported columns:</p>
                <p className="text-gray-500">
                  Name, Email, Phone, Company, Title/Position, Notes, Group/Tag, Address, Website, LinkedIn, Twitter
                </p>
                <p className="text-gray-400 mt-1">
                  Column names are auto-detected (e.g., &quot;First Name&quot;, &quot;Job Title&quot;, etc.)
                </p>
              </div>
            </div>

            {/* File Input */}
            <div className="space-y-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".csv,.xlsx,.xls"
                className="hidden"
                disabled={isImporting}
              />
              
              <div 
                onClick={() => !isImporting && fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                  ${importFile ? 'border-amber-500 bg-amber-50' : 'border-gray-300 hover:border-amber-400 hover:bg-gray-50'}
                  ${isImporting ? 'cursor-not-allowed opacity-60' : ''}`}
              >
                {importFile ? (
                  <div className="space-y-1">
                    <FileText className="w-8 h-8 mx-auto text-amber-600" />
                    <p className="font-medium text-gray-900">{importFile.name}</p>
                    <p className="text-sm text-gray-500">
                      {(importFile.size / 1024).toFixed(1)} KB
                    </p>
                    {!isImporting && (
                      <button 
                        onClick={(e) => { e.stopPropagation(); setImportFile(null); }}
                        className="text-xs text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-1">
                    <Upload className="w-8 h-8 mx-auto text-gray-400" />
                    <p className="text-gray-600">Click to select a file</p>
                    <p className="text-xs text-gray-400">CSV or Excel (max 5MB)</p>
                  </div>
                )}
              </div>
            </div>

            {/* Import Status */}
            {importStatus && (
              <div className={`rounded-lg p-3 text-sm ${
                importStatus.status === 'completed' ? 'bg-green-50 text-green-700' :
                importStatus.status === 'failed' ? 'bg-red-50 text-red-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                <div className="flex items-center gap-2">
                  {importStatus.status === 'completed' ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : importStatus.status === 'failed' ? (
                    <X className="w-4 h-4" />
                  ) : (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  )}
                  <span>{importStatus.message}</span>
                </div>
                {importStatus.details?.errors?.length > 0 && (
                  <div className="mt-2 text-xs">
                    <p className="font-medium">Errors ({importStatus.details.errors.length}):</p>
                    <ul className="list-disc ml-4 mt-1 max-h-20 overflow-y-auto">
                      {importStatus.details.errors.slice(0, 5).map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                      {importStatus.details.errors.length > 5 && (
                        <li>...and {importStatus.details.errors.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Fix Previous Imports Helper */}
            {!isImporting && !importFile && (
              <div className="text-xs text-gray-500 border-t pt-3 mt-2">
                <p className="mb-2">Having trouble viewing imported contacts?</p>
                <button
                  onClick={async () => {
                    try {
                      setIsImporting(true);
                      const result = await contactAPI.migrateImportedContacts();
                      if (result.success) {
                        toast({
                          title: "Fix Applied",
                          description: `Fixed ${result.migrated} imported contacts.`
                        });
                        loadScannedContacts(true);
                      }
                    } catch (error) {
                      console.error('Migration error:', error);
                    } finally {
                      setIsImporting(false);
                    }
                  }}
                  className="text-amber-600 hover:text-amber-700 underline"
                >
                  Click here to fix previous imports
                </button>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowImportDialog(false)}
                disabled={isImporting}
              >
                {importStatus?.status === 'completed' ? 'Close' : 'Cancel'}
              </Button>
              {importStatus?.status !== 'completed' && (
                <Button
                  onClick={handleImportSubmit}
                  disabled={!importFile || isImporting}
                  className="bg-gray-900 hover:bg-gray-800 text-white"
                >
                  {isImporting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <FileDown className="w-4 h-4 mr-2" />
                      Import Contacts
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Contacts;